export interface MedalWinner {
  gold: string;
  silver: string;
  bronze: string;
}

export interface TournamentMatchup {
  id: string;
  round: string;
  team1: string;
  team2: string;
  score1: number;
  score2: number;
  status: "completed" | "upcoming";
  winner?: string;
  isOvertime?: boolean;
  isShootout?: boolean;
  date?: string;
  time?: string;
  venue?: string;
  broadcast?: string;
}

export interface PlayerAward {
  name: string;
  country: string;
  team?: string;
  stat?: string;
}

export interface TournamentAwards {
  mvp: PlayerAward;
  topScorer: PlayerAward;
  bestGoalie: PlayerAward;
  bestDefenseman?: PlayerAward;
}

export type AllStarPosition = "G" | "LD" | "RD" | "LW" | "C" | "RW";

export interface AllStarPlayer {
  name: string;
  country: string;
  position: AllStarPosition;
  team?: string;
  stats?: string;
}

export interface AllStarTeam {
  goalie: AllStarPlayer;
  leftDefense: AllStarPlayer;
  rightDefense: AllStarPlayer;
  leftWing: AllStarPlayer;
  center: AllStarPlayer;
  rightWing: AllStarPlayer;
}

export interface GroupStanding {
  team: string;
  gp: number;
  w: number;
  otw: number;
  otl: number;
  l: number;
  gf: number;
  ga: number;
  pts: number;
}

export interface GroupMatch {
  team1: string;
  team2: string;
  score1?: number;
  score2?: number;
  date?: string;
  time?: string;
  venue?: string;
  broadcast?: string;
  gameDay?: number;
  isOvertime?: boolean;
  isShootout?: boolean;
}

export interface TournamentGroup {
  name: string;
  standings: GroupStanding[];
  matches: GroupMatch[];
}

export interface InternationalTournament {
  name: string;
  year: number;
  host: string;
  dates: string;
  medals?: MedalWinner;
  awards?: TournamentAwards;
  allStarTeam?: AllStarTeam;
  groupStage?: TournamentGroup[];
  knockoutRounds: {
    name: string;
    matchups: TournamentMatchup[];
  }[];
}

// World Championship Data
export const WORLD_CHAMPIONSHIP_2024: InternationalTournament = {
  name: "IIHF World Championship",
  year: 2024,
  host: "Czech Republic",
  dates: "May 10-26, 2024",
  medals: { gold: "CZE", silver: "SUI", bronze: "SWE" },
  awards: {
    mvp: { name: "Roman Červenka", country: "CZE", stat: "Captain, Gold Medal" },
    topScorer: { name: "Nico Hischier", country: "SUI", stat: "18 points (6G, 12A)" },
    bestGoalie: { name: "Lukáš Dostál", country: "CZE", stat: "1.29 GAA, .942 SV%" },
    bestDefenseman: { name: "Rasmus Dahlin", country: "SWE", stat: "8 points (2G, 6A)" },
  },
  allStarTeam: {
    goalie: { name: "Lukáš Dostál", country: "CZE", position: "G", stats: "1.29 GAA, .942 SV%" },
    leftDefense: { name: "Rasmus Dahlin", country: "SWE", position: "LD", stats: "8 PTS (2G, 6A)" },
    rightDefense: { name: "David Jiříček", country: "CZE", position: "RD", stats: "6 PTS (1G, 5A)" },
    leftWing: { name: "Roman Červenka", country: "CZE", position: "LW", stats: "10 PTS (3G, 7A)" },
    center: { name: "Nico Hischier", country: "SUI", position: "C", stats: "18 PTS (6G, 12A)" },
    rightWing: { name: "Tage Thompson", country: "USA", position: "RW", stats: "11 PTS (6G, 5A)" },
  },
  groupStage: [
    {
      name: "Group A",
      standings: [
        { team: "CZE", gp: 7, w: 5, otw: 1, otl: 0, l: 1, gf: 32, ga: 12, pts: 17 },
        { team: "SWE", gp: 7, w: 5, otw: 0, otl: 1, l: 1, gf: 24, ga: 15, pts: 16 },
        { team: "USA", gp: 7, w: 5, otw: 0, otl: 0, l: 2, gf: 29, ga: 16, pts: 15 },
        { team: "FIN", gp: 7, w: 4, otw: 0, otl: 1, l: 2, gf: 22, ga: 14, pts: 13 },
        { team: "NOR", gp: 7, w: 2, otw: 1, otl: 0, l: 4, gf: 16, ga: 22, pts: 8 },
        { team: "DEN", gp: 7, w: 2, otw: 0, otl: 1, l: 4, gf: 14, ga: 24, pts: 7 },
        { team: "KAZ", gp: 7, w: 1, otw: 0, otl: 1, l: 5, gf: 13, ga: 29, pts: 4 },
        { team: "GBR", gp: 7, w: 1, otw: 0, otl: 0, l: 6, gf: 11, ga: 29, pts: 3 },
      ],
      matches: [
        // Game Day 1
        { team1: "CZE", team2: "FIN", score1: 4, score2: 2, gameDay: 1, date: "May 10" },
        { team1: "SWE", team2: "GBR", score1: 5, score2: 0, gameDay: 1, date: "May 10" },
        { team1: "USA", team2: "KAZ", score1: 6, score2: 1, gameDay: 1, date: "May 10" },
        { team1: "NOR", team2: "DEN", score1: 3, score2: 2, gameDay: 1, date: "May 10", isOvertime: true },
        // Game Day 2
        { team1: "CZE", team2: "NOR", score1: 5, score2: 1, gameDay: 2, date: "May 11" },
        { team1: "FIN", team2: "GBR", score1: 6, score2: 0, gameDay: 2, date: "May 11" },
        { team1: "SWE", team2: "KAZ", score1: 4, score2: 2, gameDay: 2, date: "May 11" },
        { team1: "USA", team2: "DEN", score1: 5, score2: 1, gameDay: 2, date: "May 11" },
        // Game Day 3
        { team1: "CZE", team2: "SWE", score1: 4, score2: 2, gameDay: 3, date: "May 13" },
        { team1: "FIN", team2: "NOR", score1: 3, score2: 1, gameDay: 3, date: "May 13" },
        { team1: "USA", team2: "GBR", score1: 7, score2: 1, gameDay: 3, date: "May 13" },
        { team1: "DEN", team2: "KAZ", score1: 4, score2: 3, gameDay: 3, date: "May 13", isShootout: true },
        // Game Day 4
        { team1: "SWE", team2: "FIN", score1: 2, score2: 3, gameDay: 4, date: "May 14", isOvertime: true },
        { team1: "USA", team2: "NOR", score1: 4, score2: 1, gameDay: 4, date: "May 14" },
        { team1: "CZE", team2: "DEN", score1: 5, score2: 2, gameDay: 4, date: "May 14" },
        { team1: "GBR", team2: "KAZ", score1: 4, score2: 3, gameDay: 4, date: "May 14" },
        // Game Day 5
        { team1: "CZE", team2: "USA", score1: 3, score2: 2, gameDay: 5, date: "May 16", isOvertime: true },
        { team1: "SWE", team2: "NOR", score1: 3, score2: 2, gameDay: 5, date: "May 16" },
        { team1: "FIN", team2: "DEN", score1: 4, score2: 1, gameDay: 5, date: "May 16" },
        { team1: "GBR", team2: "DEN", score1: 2, score2: 4, gameDay: 5, date: "May 16" },
        // Game Day 6
        { team1: "SWE", team2: "DEN", score1: 3, score2: 1, gameDay: 6, date: "May 18" },
        { team1: "CZE", team2: "KAZ", score1: 6, score2: 1, gameDay: 6, date: "May 18" },
        { team1: "USA", team2: "FIN", score1: 3, score2: 2, gameDay: 6, date: "May 18", isOvertime: true },
        { team1: "NOR", team2: "GBR", score1: 5, score2: 2, gameDay: 6, date: "May 18" },
        // Game Day 7
        { team1: "CZE", team2: "GBR", score1: 5, score2: 1, gameDay: 7, date: "May 19" },
        { team1: "SWE", team2: "USA", score1: 2, score2: 3, gameDay: 7, date: "May 19" },
        { team1: "FIN", team2: "KAZ", score1: 4, score2: 2, gameDay: 7, date: "May 19" },
        { team1: "NOR", team2: "KAZ", score1: 3, score2: 2, gameDay: 7, date: "May 19", isShootout: true },
      ],
    },
    {
      name: "Group B",
      standings: [
        { team: "SUI", gp: 7, w: 6, otw: 0, otl: 1, l: 0, gf: 28, ga: 12, pts: 19 },
        { team: "CAN", gp: 7, w: 5, otw: 1, otl: 0, l: 1, gf: 34, ga: 14, pts: 17 },
        { team: "GER", gp: 7, w: 4, otw: 1, otl: 0, l: 2, gf: 25, ga: 18, pts: 14 },
        { team: "AUT", gp: 7, w: 3, otw: 0, otl: 1, l: 3, gf: 18, ga: 22, pts: 10 },
        { team: "SVK", gp: 7, w: 3, otw: 0, otl: 0, l: 4, gf: 19, ga: 21, pts: 9 },
        { team: "LAT", gp: 7, w: 2, otw: 0, otl: 1, l: 4, gf: 17, ga: 25, pts: 7 },
        { team: "FRA", gp: 7, w: 1, otw: 0, otl: 0, l: 6, gf: 11, ga: 30, pts: 3 },
        { team: "POL", gp: 7, w: 0, otw: 0, otl: 1, l: 6, gf: 12, ga: 32, pts: 1 },
      ],
      matches: [
        // Game Day 1
        { team1: "CAN", team2: "FRA", score1: 5, score2: 1, gameDay: 1, date: "May 10" },
        { team1: "SUI", team2: "LAT", score1: 4, score2: 1, gameDay: 1, date: "May 10" },
        { team1: "GER", team2: "SVK", score1: 3, score2: 2, gameDay: 1, date: "May 10" },
        { team1: "AUT", team2: "POL", score1: 4, score2: 2, gameDay: 1, date: "May 10" },
        // Game Day 2
        { team1: "SUI", team2: "GER", score1: 3, score2: 2, gameDay: 2, date: "May 11", isOvertime: true },
        { team1: "CAN", team2: "AUT", score1: 7, score2: 2, gameDay: 2, date: "May 11" },
        { team1: "SVK", team2: "POL", score1: 5, score2: 1, gameDay: 2, date: "May 11" },
        { team1: "LAT", team2: "FRA", score1: 4, score2: 3, gameDay: 2, date: "May 11", isShootout: true },
        // Game Day 3
        { team1: "CAN", team2: "SVK", score1: 6, score2: 3, gameDay: 3, date: "May 13" },
        { team1: "SUI", team2: "AUT", score1: 4, score2: 1, gameDay: 3, date: "May 13" },
        { team1: "GER", team2: "FRA", score1: 5, score2: 0, gameDay: 3, date: "May 13" },
        { team1: "LAT", team2: "POL", score1: 3, score2: 2, gameDay: 3, date: "May 13" },
        // Game Day 4
        { team1: "SUI", team2: "CAN", score1: 3, score2: 2, gameDay: 4, date: "May 14", isShootout: true },
        { team1: "GER", team2: "AUT", score1: 4, score2: 1, gameDay: 4, date: "May 14" },
        { team1: "SVK", team2: "LAT", score1: 2, score2: 3, gameDay: 4, date: "May 14" },
        { team1: "FRA", team2: "POL", score1: 3, score2: 2, gameDay: 4, date: "May 14" },
        // Game Day 5
        { team1: "CAN", team2: "GER", score1: 3, score2: 4, gameDay: 5, date: "May 16" },
        { team1: "SUI", team2: "SVK", score1: 5, score2: 1, gameDay: 5, date: "May 16" },
        { team1: "AUT", team2: "LAT", score1: 2, score2: 3, gameDay: 5, date: "May 16", isOvertime: true },
        { team1: "GER", team2: "POL", score1: 8, score2: 1, gameDay: 5, date: "May 16" },
        // Game Day 6
        { team1: "CAN", team2: "LAT", score1: 6, score2: 2, gameDay: 6, date: "May 18" },
        { team1: "SUI", team2: "FRA", score1: 5, score2: 1, gameDay: 6, date: "May 18" },
        { team1: "GER", team2: "LAT", score1: 5, score2: 2, gameDay: 6, date: "May 18" },
        { team1: "AUT", team2: "SVK", score1: 3, score2: 4, gameDay: 6, date: "May 18" },
        // Game Day 7
        { team1: "CAN", team2: "POL", score1: 7, score2: 1, gameDay: 7, date: "May 19" },
        { team1: "SUI", team2: "POL", score1: 6, score2: 2, gameDay: 7, date: "May 19" },
        { team1: "SVK", team2: "FRA", score1: 4, score2: 2, gameDay: 7, date: "May 19" },
        { team1: "AUT", team2: "FRA", score1: 3, score2: 1, gameDay: 7, date: "May 19" },
      ],
    },
  ],
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "CZE", team2: "USA", score1: 1, score2: 0, status: "completed", winner: "CZE" },
        { id: "qf2", round: "QF", team1: "CAN", team2: "SUI", score1: 2, score2: 3, status: "completed", winner: "SUI", isOvertime: true },
        { id: "qf3", round: "QF", team1: "SWE", team2: "GER", score1: 4, score2: 3, status: "completed", winner: "SWE", isShootout: true },
        { id: "qf4", round: "QF", team1: "FIN", team2: "SUI", score1: 2, score2: 3, status: "completed", winner: "SUI" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "CZE", team2: "SWE", score1: 7, score2: 3, status: "completed", winner: "CZE" },
        { id: "sf2", round: "SF", team1: "SUI", team2: "CAN", score1: 3, score2: 2, status: "completed", winner: "SUI", isOvertime: true },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "SWE", team2: "CAN", score1: 4, score2: 2, status: "completed", winner: "SWE" },
        { id: "gold", round: "Gold", team1: "CZE", team2: "SUI", score1: 2, score2: 0, status: "completed", winner: "CZE" },
      ],
    },
  ],
};

// World Championship 2026 - Completed (Switzerland) — Gold: FIN, Silver: SUI, Bronze: NOR
export const WORLD_CHAMPIONSHIP_2026: InternationalTournament = {
  name: "IIHF World Championship",
  year: 2026,
  host: "Zurich & Fribourg, Switzerland",
  dates: "May 15-31, 2026",
  medals: { gold: "FIN", silver: "SUI", bronze: "NOR" },
  groupStage: [
    {
      name: "Group A (Zurich)",
      standings: [
        { team: "SUI", gp: 7, w: 7, otw: 0, otl: 0, l: 0, gf: 39, ga: 7, pts: 21 },
        { team: "FIN", gp: 7, w: 6, otw: 0, otl: 0, l: 1, gf: 31, ga: 11, pts: 18 },
        { team: "LAT", gp: 7, w: 4, otw: 0, otl: 0, l: 3, gf: 24, ga: 17, pts: 12 },
        { team: "USA", gp: 7, w: 3, otw: 1, otl: 0, l: 3, gf: 25, ga: 21, pts: 11 },
        { team: "GER", gp: 7, w: 3, otw: 0, otl: 1, l: 3, gf: 23, ga: 22, pts: 10 },
        { team: "AUT", gp: 7, w: 3, otw: 0, otl: 0, l: 4, gf: 17, ga: 29, pts: 9 },
        { team: "HUN", gp: 7, w: 1, otw: 0, otl: 0, l: 6, gf: 14, ga: 38, pts: 3 },
        { team: "GBR", gp: 7, w: 0, otw: 0, otl: 0, l: 7, gf: 7, ga: 35, pts: 0 },
      ],
      matches: [
        { team1: "FIN", team2: "GER", score1: 3, score2: 1, date: "May 15", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 1 },
        { team1: "USA", team2: "SUI", score1: 1, score2: 3, date: "May 15", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 1 },
        { team1: "GBR", team2: "AUT", score1: 2, score2: 5, date: "May 16", time: "12:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 2 },
        { team1: "HUN", team2: "FIN", score1: 1, score2: 4, date: "May 16", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 2 },
        { team1: "SUI", team2: "LAT", score1: 4, score2: 2, date: "May 16", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 2 },
        { team1: "GBR", team2: "USA", score1: 1, score2: 5, date: "May 17", time: "12:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 3 },
        { team1: "AUT", team2: "HUN", score1: 4, score2: 2, date: "May 17", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 3 },
        { team1: "GER", team2: "LAT", score1: 0, score2: 2, date: "May 17", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 3 },
        { team1: "FIN", team2: "USA", score1: 6, score2: 2, date: "May 18", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 4 },
        { team1: "GER", team2: "SUI", score1: 1, score2: 6, date: "May 18", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 4 },
        { team1: "LAT", team2: "AUT", score1: 1, score2: 3, date: "May 19", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 5 },
        { team1: "HUN", team2: "GBR", score1: 5, score2: 0, date: "May 19", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 5 },
        { team1: "AUT", team2: "SUI", score1: 0, score2: 9, date: "May 20", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 6 },
        { team1: "USA", team2: "GER", score1: 4, score2: 3, date: "May 20", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 6, isShootout: true },
        { team1: "LAT", team2: "FIN", score1: 1, score2: 7, date: "May 21", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 7 },
        { team1: "SUI", team2: "GBR", score1: 4, score2: 1, date: "May 21", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 7 },
        { team1: "GER", team2: "HUN", score1: 6, score2: 2, date: "May 22", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 8 },
        { team1: "FIN", team2: "GBR", score1: 4, score2: 0, date: "May 22", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 8 },
        { team1: "LAT", team2: "USA", score1: 4, score2: 2, date: "May 23", time: "12:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 9 },
        { team1: "SUI", team2: "HUN", score1: 9, score2: 0, date: "May 23", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 9 },
        { team1: "AUT", team2: "GER", score1: 2, score2: 6, date: "May 23", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 9 },
        { team1: "GBR", team2: "LAT", score1: 0, score2: 6, date: "May 24", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 10 },
        { team1: "FIN", team2: "AUT", score1: 5, score2: 2, date: "May 24", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 10 },
        { team1: "USA", team2: "HUN", score1: 7, score2: 3, date: "May 25", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 11 },
        { team1: "GER", team2: "GBR", score1: 6, score2: 3, date: "May 25", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 11 },
        { team1: "HUN", team2: "LAT", score1: 1, score2: 8, date: "May 26", time: "12:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 12 },
        { team1: "USA", team2: "AUT", score1: 4, score2: 1, date: "May 26", time: "16:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 12 },
        { team1: "SUI", team2: "FIN", score1: 4, score2: 2, date: "May 26", time: "20:20 CET", venue: "Swiss Life Arena, Zurich", gameDay: 12 },
      ],
    },
    {
      name: "Group B (Fribourg)",
      standings: [
        { team: "CAN", gp: 7, w: 6, otw: 1, otl: 0, l: 0, gf: 33, ga: 13, pts: 20 },
        { team: "NOR", gp: 7, w: 4, otw: 1, otl: 1, l: 1, gf: 25, ga: 14, pts: 15 },
        { team: "CZE", gp: 7, w: 4, otw: 0, otl: 1, l: 2, gf: 19, ga: 17, pts: 13 },
        { team: "SWE", gp: 7, w: 4, otw: 0, otl: 0, l: 3, gf: 27, ga: 16, pts: 12 },
        { team: "SVK", gp: 7, w: 3, otw: 1, otl: 0, l: 3, gf: 21, ga: 19, pts: 11 },
        { team: "DEN", gp: 7, w: 1, otw: 1, otl: 1, l: 4, gf: 15, ga: 26, pts: 6 },
        { team: "SLO", gp: 7, w: 1, otw: 1, otl: 1, l: 4, gf: 13, ga: 25, pts: 6 },
        { team: "ITA", gp: 7, w: 0, otw: 0, otl: 1, l: 6, gf: 5, ga: 28, pts: 1 },
      ],
      matches: [
        { team1: "CAN", team2: "SWE", score1: 5, score2: 3, date: "May 15", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 1 },
        { team1: "CZE", team2: "DEN", score1: 4, score2: 1, date: "May 15", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 1 },
        { team1: "SVK", team2: "NOR", score1: 2, score2: 1, date: "May 16", time: "12:20 CET", venue: "BCF Arena, Fribourg", gameDay: 2 },
        { team1: "ITA", team2: "CAN", score1: 0, score2: 6, date: "May 16", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 2 },
        { team1: "SLO", team2: "CZE", score1: 3, score2: 2, date: "May 16", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 2, isOvertime: true },
        { team1: "ITA", team2: "SVK", score1: 1, score2: 4, date: "May 17", time: "12:20 CET", venue: "BCF Arena, Fribourg", gameDay: 3 },
        { team1: "DEN", team2: "SWE", score1: 2, score2: 6, date: "May 17", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 3 },
        { team1: "NOR", team2: "SLO", score1: 4, score2: 0, date: "May 17", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 3 },
        { team1: "CAN", team2: "DEN", score1: 5, score2: 1, date: "May 18", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 4 },
        { team1: "SWE", team2: "CZE", score1: 3, score2: 4, date: "May 18", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 4 },
        { team1: "ITA", team2: "NOR", score1: 0, score2: 4, date: "May 19", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 5 },
        { team1: "SLO", team2: "SVK", score1: 4, score2: 5, date: "May 19", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 5, isShootout: true },
        { team1: "CZE", team2: "ITA", score1: 3, score2: 1, date: "May 20", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 6 },
        { team1: "SWE", team2: "SLO", score1: 6, score2: 0, date: "May 20", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 6 },
        { team1: "CAN", team2: "NOR", score1: 6, score2: 5, date: "May 21", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 7, isOvertime: true },
        { team1: "DEN", team2: "SVK", score1: 1, score2: 5, date: "May 21", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 7 },
        { team1: "CAN", team2: "SLO", score1: 3, score2: 1, date: "May 22", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 8 },
        { team1: "SWE", team2: "ITA", score1: 3, score2: 0, date: "May 22", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 8 },
        { team1: "DEN", team2: "SLO", score1: 4, score2: 0, date: "May 23", time: "12:20 CET", venue: "BCF Arena, Fribourg", gameDay: 9 },
        { team1: "SVK", team2: "CZE", score1: 2, score2: 3, date: "May 23", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 9 },
        { team1: "NOR", team2: "SWE", score1: 3, score2: 2, date: "May 23", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 9 },
        { team1: "DEN", team2: "ITA", score1: 3, score2: 2, date: "May 24", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 10, isShootout: true },
        { team1: "SVK", team2: "CAN", score1: 1, score2: 5, date: "May 24", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 10 },
        { team1: "CZE", team2: "NOR", score1: 1, score2: 4, date: "May 25", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 11 },
        { team1: "SLO", team2: "ITA", score1: 5, score2: 1, date: "May 25", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 11 },
        { team1: "NOR", team2: "DEN", score1: 4, score2: 3, date: "May 26", time: "12:20 CET", venue: "BCF Arena, Fribourg", gameDay: 12, isOvertime: true },
        { team1: "SWE", team2: "SVK", score1: 4, score2: 2, date: "May 26", time: "16:20 CET", venue: "BCF Arena, Fribourg", gameDay: 12 },
        { team1: "CZE", team2: "CAN", score1: 2, score2: 3, date: "May 26", time: "20:20 CET", venue: "BCF Arena, Fribourg", gameDay: 12 },
      ],
    },
  ],
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "FIN", team2: "CZE", score1: 4, score2: 1, status: "completed", winner: "FIN", date: "May 28", time: "16:20 CET", venue: "Swiss Life Arena, Zurich" },
        { id: "qf2", round: "QF", team1: "CAN", team2: "USA", score1: 4, score2: 0, status: "completed", winner: "CAN", date: "May 28", time: "16:20 CET", venue: "BCF Arena, Fribourg" },
        { id: "qf3", round: "QF", team1: "SUI", team2: "SWE", score1: 3, score2: 1, status: "completed", winner: "SUI", date: "May 28", time: "20:20 CET", venue: "Swiss Life Arena, Zurich" },
        { id: "qf4", round: "QF", team1: "NOR", team2: "LAT", score1: 2, score2: 0, status: "completed", winner: "NOR", date: "May 28", time: "20:20 CET", venue: "BCF Arena, Fribourg" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "SUI", team2: "NOR", score1: 6, score2: 0, status: "completed", winner: "SUI", date: "May 30", time: "15:20 CET", venue: "Swiss Life Arena, Zurich" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "FIN", score1: 2, score2: 4, status: "completed", winner: "FIN", date: "May 30", time: "20:00 CET", venue: "Swiss Life Arena, Zurich" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CAN", team2: "NOR", score1: 2, score2: 3, status: "completed", winner: "NOR", isOvertime: true, date: "May 31", time: "15:30 CET", venue: "Swiss Life Arena, Zurich" },
        { id: "gold", round: "Gold", team1: "SUI", team2: "FIN", score1: 0, score2: 1, status: "completed", winner: "FIN", isOvertime: true, date: "May 31", time: "20:20 CET", venue: "Swiss Life Arena, Zurich" },
      ],
    },
  ],
};

// World Championship 2025 - Completed
export const WORLD_CHAMPIONSHIP_2025: InternationalTournament = {
  name: "IIHF World Championship",
  year: 2025,
  host: "Sweden/Denmark",
  dates: "May 9-25, 2025",
  medals: { gold: "FIN", silver: "SWE", bronze: "CAN" },
  awards: {
    mvp: { name: "Mikko Rantanen", country: "FIN", stat: "Tournament MVP" },
    topScorer: { name: "Mikko Rantanen", country: "FIN", stat: "16 points (7G, 9A)" },
    bestGoalie: { name: "Ukko-Pekka Luukkonen", country: "FIN", stat: "1.42 GAA, .938 SV%" },
    bestDefenseman: { name: "Rasmus Dahlin", country: "SWE", stat: "9 points (3G, 6A)" },
  },
  allStarTeam: {
    goalie: { name: "Ukko-Pekka Luukkonen", country: "FIN", position: "G", stats: "1.42 GAA, .938 SV%" },
    leftDefense: { name: "Rasmus Dahlin", country: "SWE", position: "LD", stats: "9 PTS (3G, 6A)" },
    rightDefense: { name: "Esa Lindell", country: "FIN", position: "RD", stats: "6 PTS (1G, 5A)" },
    leftWing: { name: "Mikko Rantanen", country: "FIN", position: "LW", stats: "16 PTS (7G, 9A)" },
    center: { name: "Elias Pettersson", country: "SWE", position: "C", stats: "12 PTS (5G, 7A)" },
    rightWing: { name: "Brady Tkachuk", country: "USA", position: "RW", stats: "10 PTS (4G, 6A)" },
  },
  groupStage: [
    {
      name: "Group A (Stockholm)",
      standings: [
        { team: "CAN", gp: 3, w: 3, otw: 0, otl: 0, l: 0, gf: 16, ga: 1, pts: 9 },
        { team: "SWE", gp: 3, w: 3, otw: 0, otl: 0, l: 0, gf: 11, ga: 3, pts: 9 },
        { team: "LAT", gp: 3, w: 2, otw: 0, otl: 0, l: 1, gf: 10, ga: 10, pts: 6 },
        { team: "FIN", gp: 3, w: 1, otw: 1, otl: 0, l: 1, gf: 7, ga: 6, pts: 5 },
        { team: "SVK", gp: 3, w: 1, otw: 0, otl: 1, l: 1, gf: 5, ga: 9, pts: 4 },
        { team: "AUT", gp: 3, w: 0, otw: 1, otl: 0, l: 2, gf: 5, ga: 6, pts: 2 },
        { team: "FRA", gp: 3, w: 0, otw: 0, otl: 1, l: 2, gf: 4, ga: 13, pts: 1 },
        { team: "SLO", gp: 3, w: 0, otw: 0, otl: 0, l: 3, gf: 3, ga: 12, pts: 0 },
      ],
      matches: [
        // Day 5 (May 13, 2025) – Avicii Arena, Stockholm
        { team1: "SLO", team2: "LAT", score1: 2, score2: 5, date: "May 13", time: "16:20 CEST", venue: "Avicii Arena, Stockholm", gameDay: 5 },
        { team1: "CAN", team2: "FRA", score1: 5, score2: 0, date: "May 13", time: "20:20 CEST", venue: "Avicii Arena, Stockholm", gameDay: 5 },
      ],
    },
    {
      name: "Group B (Herning)",
      standings: [
        { team: "GER", gp: 3, w: 3, otw: 0, otl: 0, l: 0, gf: 15, ga: 4, pts: 9 },
        { team: "CZE", gp: 3, w: 2, otw: 1, otl: 0, l: 0, gf: 14, ga: 7, pts: 8 },
        { team: "SUI", gp: 3, w: 2, otw: 0, otl: 1, l: 0, gf: 12, ga: 7, pts: 7 },
        { team: "USA", gp: 3, w: 2, otw: 0, otl: 0, l: 1, gf: 11, ga: 3, pts: 6 },
        { team: "HUN", gp: 3, w: 1, otw: 0, otl: 0, l: 2, gf: 5, ga: 14, pts: 3 },
        { team: "KAZ", gp: 3, w: 1, otw: 0, otl: 0, l: 2, gf: 5, ga: 9, pts: 3 },
        { team: "NOR", gp: 3, w: 0, otw: 0, otl: 0, l: 3, gf: 4, ga: 9, pts: 0 },
        { team: "DEN", gp: 3, w: 0, otw: 0, otl: 0, l: 3, gf: 4, ga: 17, pts: 0 },
      ],
      matches: [
        // Day 5 (May 13, 2025) – Jyske Bank Boxen, Herning
        { team1: "NOR", team2: "GER", score1: 2, score2: 5, date: "May 13", time: "16:20 CEST", venue: "Jyske Bank Boxen, Herning", gameDay: 5 },
        { team1: "KAZ", team2: "HUN", score1: 2, score2: 4, date: "May 13", time: "20:20 CEST", venue: "Jyske Bank Boxen, Herning", gameDay: 5 },
      ],
    },
  ],
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "FIN", team2: "USA", score1: 4, score2: 1, status: "completed", winner: "FIN" },
        { id: "qf2", round: "QF", team1: "SWE", team2: "GER", score1: 5, score2: 2, status: "completed", winner: "SWE" },
        { id: "qf3", round: "QF", team1: "CAN", team2: "CZE", score1: 3, score2: 2, status: "completed", winner: "CAN", isOvertime: true },
        { id: "qf4", round: "QF", team1: "SUI", team2: "SVK", score1: 4, score2: 3, status: "completed", winner: "SUI", isShootout: true },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "FIN", team2: "SUI", score1: 5, score2: 1, status: "completed", winner: "FIN" },
        { id: "sf2", round: "SF", team1: "SWE", team2: "CAN", score1: 4, score2: 2, status: "completed", winner: "SWE" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CAN", team2: "SUI", score1: 6, score2: 2, status: "completed", winner: "CAN" },
        { id: "gold", round: "Gold", team1: "FIN", team2: "SWE", score1: 4, score2: 3, status: "completed", winner: "FIN", isOvertime: true },
      ],
    },
  ],
};

export const WORLD_CHAMPIONSHIP_2023: InternationalTournament = {
  name: "IIHF World Championship",
  year: 2023,
  host: "Finland/Latvia",
  dates: "May 12-28, 2023",
  medals: { gold: "CAN", silver: "GER", bronze: "USA" },
  awards: {
    mvp: { name: "Connor Bedard", country: "CAN", stat: "Youngest WC MVP" },
    topScorer: { name: "Connor Bedard", country: "CAN", stat: "14 points (8G, 6A)" },
    bestGoalie: { name: "Jonas Johansson", country: "SWE", stat: "1.53 GAA, .932 SV%" },
    bestDefenseman: { name: "Moritz Seider", country: "GER", stat: "10 points (2G, 8A)" },
  },
  allStarTeam: {
    goalie: { name: "Jonas Johansson", country: "SWE", position: "G", stats: "1.53 GAA, .932 SV%" },
    leftDefense: { name: "Moritz Seider", country: "GER", position: "LD", stats: "10 PTS (2G, 8A)" },
    rightDefense: { name: "Thomas Chabot", country: "CAN", position: "RD", stats: "7 PTS (1G, 6A)" },
    leftWing: { name: "J.J. Peterka", country: "GER", position: "LW", stats: "9 PTS (5G, 4A)" },
    center: { name: "Connor Bedard", country: "CAN", position: "C", stats: "14 PTS (8G, 6A)" },
    rightWing: { name: "Dylan Cozens", country: "CAN", position: "RW", stats: "12 PTS (5G, 7A)" },
  },
  groupStage: [
    {
      name: "Group A",
      standings: [
        { team: "CAN", gp: 7, w: 6, otw: 0, otl: 0, l: 1, gf: 38, ga: 15, pts: 18 },
        { team: "GER", gp: 7, w: 5, otw: 0, otl: 1, l: 1, gf: 29, ga: 17, pts: 16 },
        { team: "SWE", gp: 7, w: 4, otw: 1, otl: 0, l: 2, gf: 26, ga: 16, pts: 14 },
        { team: "USA", gp: 7, w: 4, otw: 0, otl: 1, l: 2, gf: 24, ga: 14, pts: 13 },
        { team: "NOR", gp: 7, w: 2, otw: 1, otl: 1, l: 3, gf: 18, ga: 23, pts: 9 },
        { team: "AUT", gp: 7, w: 2, otw: 0, otl: 0, l: 5, gf: 13, ga: 31, pts: 6 },
        { team: "HUN", gp: 7, w: 1, otw: 0, otl: 1, l: 5, gf: 12, ga: 34, pts: 4 },
        { team: "KAZ", gp: 7, w: 0, otw: 0, otl: 0, l: 7, gf: 11, ga: 41, pts: 0 },
      ],
      matches: [
        // Game Day 1
        { team1: "CAN", team2: "GER", score1: 5, score2: 2, gameDay: 1, date: "May 12" },
        { team1: "SWE", team2: "HUN", score1: 7, score2: 1, gameDay: 1, date: "May 12" },
        { team1: "USA", team2: "AUT", score1: 4, score2: 1, gameDay: 1, date: "May 12" },
        { team1: "NOR", team2: "KAZ", score1: 3, score2: 1, gameDay: 1, date: "May 12" },
        // Game Day 2
        { team1: "CAN", team2: "SWE", score1: 4, score2: 3, gameDay: 2, date: "May 13", isOvertime: true },
        { team1: "GER", team2: "AUT", score1: 6, score2: 2, gameDay: 2, date: "May 13" },
        { team1: "USA", team2: "HUN", score1: 5, score2: 0, gameDay: 2, date: "May 13" },
        { team1: "NOR", team2: "AUT", score1: 4, score2: 2, gameDay: 2, date: "May 13" },
        // Game Day 3
        { team1: "CAN", team2: "NOR", score1: 6, score2: 2, gameDay: 3, date: "May 15" },
        { team1: "SWE", team2: "USA", score1: 3, score2: 2, gameDay: 3, date: "May 15", isOvertime: true },
        { team1: "GER", team2: "KAZ", score1: 7, score2: 1, gameDay: 3, date: "May 15" },
        { team1: "HUN", team2: "AUT", score1: 3, score2: 4, gameDay: 3, date: "May 15" },
        // Game Day 4
        { team1: "CAN", team2: "USA", score1: 5, score2: 2, gameDay: 4, date: "May 16" },
        { team1: "GER", team2: "SWE", score1: 2, score2: 3, gameDay: 4, date: "May 16" },
        { team1: "NOR", team2: "HUN", score1: 4, score2: 3, gameDay: 4, date: "May 16", isShootout: true },
        { team1: "AUT", team2: "KAZ", score1: 3, score2: 1, gameDay: 4, date: "May 16" },
        // Game Day 5
        { team1: "CAN", team2: "AUT", score1: 8, score2: 1, gameDay: 5, date: "May 18" },
        { team1: "SWE", team2: "NOR", score1: 4, score2: 2, gameDay: 5, date: "May 18" },
        { team1: "GER", team2: "USA", score1: 4, score2: 2, gameDay: 5, date: "May 18" },
        { team1: "HUN", team2: "KAZ", score1: 3, score2: 2, gameDay: 5, date: "May 18" },
        // Game Day 6
        { team1: "CAN", team2: "HUN", score1: 6, score2: 1, gameDay: 6, date: "May 20" },
        { team1: "SWE", team2: "AUT", score1: 5, score2: 1, gameDay: 6, date: "May 20" },
        { team1: "USA", team2: "NOR", score1: 4, score2: 1, gameDay: 6, date: "May 20" },
        { team1: "GER", team2: "HUN", score1: 6, score2: 3, gameDay: 6, date: "May 20" },
        // Game Day 7
        { team1: "CAN", team2: "KAZ", score1: 5, score2: 1, gameDay: 7, date: "May 21" },
        { team1: "SWE", team2: "KAZ", score1: 6, score2: 2, gameDay: 7, date: "May 21" },
        { team1: "USA", team2: "KAZ", score1: 7, score2: 1, gameDay: 7, date: "May 21" },
        { team1: "NOR", team2: "GER", score1: 2, score2: 3, gameDay: 7, date: "May 21", isOvertime: true },
      ],
    },
    {
      name: "Group B",
      standings: [
        { team: "LAT", gp: 7, w: 5, otw: 0, otl: 1, l: 1, gf: 24, ga: 16, pts: 16 },
        { team: "FIN", gp: 7, w: 5, otw: 0, otl: 0, l: 2, gf: 28, ga: 14, pts: 15 },
        { team: "CZE", gp: 7, w: 4, otw: 0, otl: 1, l: 2, gf: 23, ga: 18, pts: 13 },
        { team: "SUI", gp: 7, w: 4, otw: 0, otl: 0, l: 3, gf: 22, ga: 17, pts: 12 },
        { team: "SVK", gp: 7, w: 3, otw: 1, otl: 0, l: 3, gf: 21, ga: 21, pts: 11 },
        { team: "DEN", gp: 7, w: 2, otw: 0, otl: 1, l: 4, gf: 16, ga: 24, pts: 7 },
        { team: "FRA", gp: 7, w: 1, otw: 0, otl: 1, l: 5, gf: 12, ga: 26, pts: 4 },
        { team: "SLO", gp: 7, w: 0, otw: 1, otl: 0, l: 6, gf: 10, ga: 30, pts: 2 },
      ],
      matches: [
        // Game Day 1
        { team1: "LAT", team2: "FIN", score1: 2, score2: 4, gameDay: 1, date: "May 12" },
        { team1: "CZE", team2: "SUI", score1: 3, score2: 2, gameDay: 1, date: "May 12" },
        { team1: "SVK", team2: "DEN", score1: 4, score2: 1, gameDay: 1, date: "May 12" },
        { team1: "FRA", team2: "SLO", score1: 2, score2: 3, gameDay: 1, date: "May 12", isOvertime: true },
        // Game Day 2
        { team1: "LAT", team2: "CZE", score1: 3, score2: 2, gameDay: 2, date: "May 13", isShootout: true },
        { team1: "FIN", team2: "SVK", score1: 5, score2: 1, gameDay: 2, date: "May 13" },
        { team1: "SUI", team2: "DEN", score1: 4, score2: 2, gameDay: 2, date: "May 13" },
        { team1: "FRA", team2: "DEN", score1: 2, score2: 3, gameDay: 2, date: "May 13" },
        // Game Day 3
        { team1: "LAT", team2: "SUI", score1: 4, score2: 2, gameDay: 3, date: "May 15" },
        { team1: "FIN", team2: "CZE", score1: 3, score2: 2, gameDay: 3, date: "May 15" },
        { team1: "SVK", team2: "SLO", score1: 5, score2: 1, gameDay: 3, date: "May 15" },
        { team1: "DEN", team2: "SLO", score1: 4, score2: 2, gameDay: 3, date: "May 15" },
        // Game Day 4
        { team1: "LAT", team2: "SVK", score1: 3, score2: 2, gameDay: 4, date: "May 16", isShootout: true },
        { team1: "FIN", team2: "SUI", score1: 4, score2: 1, gameDay: 4, date: "May 16" },
        { team1: "CZE", team2: "DEN", score1: 3, score2: 1, gameDay: 4, date: "May 16" },
        { team1: "FRA", team2: "SLO", score1: 3, score2: 2, gameDay: 4, date: "May 16" },
        // Game Day 5
        { team1: "LAT", team2: "FRA", score1: 4, score2: 1, gameDay: 5, date: "May 18" },
        { team1: "FIN", team2: "DEN", score1: 5, score2: 2, gameDay: 5, date: "May 18" },
        { team1: "CZE", team2: "SVK", score1: 4, score2: 3, gameDay: 5, date: "May 18", isOvertime: true },
        { team1: "SUI", team2: "SLO", score1: 3, score2: 1, gameDay: 5, date: "May 18" },
        // Game Day 6
        { team1: "LAT", team2: "DEN", score1: 3, score2: 2, gameDay: 6, date: "May 20" },
        { team1: "FIN", team2: "SLO", score1: 6, score2: 0, gameDay: 6, date: "May 20" },
        { team1: "CZE", team2: "FRA", score1: 5, score2: 2, gameDay: 6, date: "May 20" },
        { team1: "SUI", team2: "SVK", score1: 2, score2: 3, gameDay: 6, date: "May 20" },
        // Game Day 7
        { team1: "LAT", team2: "SLO", score1: 5, score2: 1, gameDay: 7, date: "May 21" },
        { team1: "FIN", team2: "FRA", score1: 4, score2: 2, gameDay: 7, date: "May 21" },
        { team1: "CZE", team2: "SLO", score1: 3, score2: 1, gameDay: 7, date: "May 21" },
        { team1: "SUI", team2: "FRA", score1: 4, score2: 1, gameDay: 7, date: "May 21" },
      ],
    },
  ],
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "CAN", team2: "SUI", score1: 6, score2: 3, status: "completed", winner: "CAN" },
        { id: "qf2", round: "QF", team1: "USA", team2: "CZE", score1: 3, score2: 2, status: "completed", winner: "USA", isOvertime: true },
        { id: "qf3", round: "QF", team1: "GER", team2: "SWE", score1: 5, score2: 4, status: "completed", winner: "GER", isShootout: true },
        { id: "qf4", round: "QF", team1: "LAT", team2: "FIN", score1: 2, score2: 1, status: "completed", winner: "LAT", isOvertime: true },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "CAN", team2: "LAT", score1: 4, score2: 2, status: "completed", winner: "CAN" },
        { id: "sf2", round: "SF", team1: "GER", team2: "USA", score1: 4, score2: 3, status: "completed", winner: "GER", isOvertime: true },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "USA", team2: "LAT", score1: 8, score2: 5, status: "completed", winner: "USA" },
        { id: "gold", round: "Gold", team1: "CAN", team2: "GER", score1: 5, score2: 2, status: "completed", winner: "CAN" },
      ],
    },
  ],
};

// Olympics Data
export const OLYMPICS_2026: InternationalTournament = {
  name: "Winter Olympics",
  year: 2026,
  host: "Milan-Cortina, Italy",
  dates: "February 11-22, 2026",
  medals: { gold: "USA", silver: "CAN", bronze: "FIN" },
  awards: {
    mvp: { name: "Auston Matthews", country: "USA", stat: "Tournament MVP, Gold Medal" },
    topScorer: { name: "Connor McDavid", country: "CAN", stat: "14 points (5G, 9A)" },
    bestGoalie: { name: "Connor Hellebuyck", country: "USA", stat: "1.33 GAA, .944 SV%" },
    bestDefenseman: { name: "Cale Makar", country: "CAN", stat: "9 points (3G, 6A)" },
  },
  allStarTeam: {
    goalie: { name: "Connor Hellebuyck", country: "USA", position: "G", stats: "1.33 GAA, .944 SV%" },
    leftDefense: { name: "Cale Makar", country: "CAN", position: "LD", stats: "9 PTS (3G, 6A)" },
    rightDefense: { name: "Miro Heiskanen", country: "FIN", position: "RD", stats: "6 PTS (1G, 5A)" },
    leftWing: { name: "Brady Tkachuk", country: "USA", position: "LW", stats: "10 PTS (4G, 6A)" },
    center: { name: "Auston Matthews", country: "USA", position: "C", stats: "12 PTS (7G, 5A)" },
    rightWing: { name: "Connor McDavid", country: "CAN", position: "RW", stats: "14 PTS (5G, 9A)" },
  },
  groupStage: [
    {
      name: "Group A",
      standings: [
        { team: "CAN", gp: 3, w: 3, otw: 0, otl: 0, l: 0, gf: 14, ga: 4, pts: 9 },
        { team: "CZE", gp: 3, w: 2, otw: 0, otl: 0, l: 1, gf: 8, ga: 6, pts: 6 },
        { team: "SUI", gp: 3, w: 1, otw: 0, otl: 0, l: 2, gf: 5, ga: 8, pts: 3 },
        { team: "FRA", gp: 3, w: 0, otw: 0, otl: 0, l: 3, gf: 3, ga: 12, pts: 0 },
      ],
      matches: [
        { team1: "SUI", team2: "FRA", score1: 3, score2: 1, gameDay: 1, date: "Feb 12", time: "12:10", venue: "Santa Giulia", broadcast: "NBC/Peacock" },
        { team1: "CZE", team2: "CAN", score1: 2, score2: 4, gameDay: 1, date: "Feb 12", time: "16:40", venue: "Santa Giulia", broadcast: "NBC/Peacock" },
        { team1: "FRA", team2: "CZE", score1: 1, score2: 3, gameDay: 2, date: "Feb 13", time: "16:40", venue: "Santa Giulia", broadcast: "Peacock" },
        { team1: "CAN", team2: "SUI", score1: 5, score2: 1, gameDay: 2, date: "Feb 13", time: "21:10", venue: "Santa Giulia", broadcast: "USA Network" },
        { team1: "SUI", team2: "CZE", score1: 1, score2: 3, gameDay: 3, date: "Feb 15", time: "12:10", venue: "Santa Giulia", broadcast: "Peacock" },
        { team1: "CAN", team2: "FRA", score1: 5, score2: 1, gameDay: 3, date: "Feb 15", time: "16:40", venue: "Santa Giulia", broadcast: "NBC/Peacock" },
      ],
    },
    {
      name: "Group B",
      standings: [
        { team: "FIN", gp: 3, w: 2, otw: 1, otl: 0, l: 0, gf: 10, ga: 5, pts: 8 },
        { team: "SWE", gp: 3, w: 2, otw: 0, otl: 1, l: 0, gf: 11, ga: 4, pts: 7 },
        { team: "SVK", gp: 3, w: 1, otw: 0, otl: 0, l: 2, gf: 5, ga: 9, pts: 3 },
        { team: "ITA", gp: 3, w: 0, otw: 0, otl: 0, l: 3, gf: 3, ga: 11, pts: 0 },
      ],
      matches: [
        { team1: "SVK", team2: "FIN", score1: 2, score2: 3, gameDay: 1, date: "Feb 11", time: "16:40", venue: "Santa Giulia", broadcast: "USA Network", isOvertime: true },
        { team1: "SWE", team2: "ITA", score1: 5, score2: 1, gameDay: 1, date: "Feb 11", time: "21:10", venue: "Santa Giulia", broadcast: "NBC/Peacock" },
        { team1: "FIN", team2: "SWE", score1: 3, score2: 4, gameDay: 2, date: "Feb 13", time: "12:10", venue: "Santa Giulia", broadcast: "Peacock", isOvertime: true },
        { team1: "ITA", team2: "SVK", score1: 1, score2: 2, gameDay: 2, date: "Feb 13", time: "12:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "SWE", team2: "SVK", score1: 2, score2: 1, gameDay: 3, date: "Feb 14", time: "12:10", venue: "Santa Giulia", broadcast: "Peacock" },
        { team1: "FIN", team2: "ITA", score1: 4, score2: 1, gameDay: 3, date: "Feb 14", time: "16:40", venue: "Santa Giulia", broadcast: "NBC/Peacock" },
      ],
    },
    {
      name: "Group C",
      standings: [
        { team: "USA", gp: 3, w: 3, otw: 0, otl: 0, l: 0, gf: 15, ga: 3, pts: 9 },
        { team: "GER", gp: 3, w: 2, otw: 0, otl: 0, l: 1, gf: 9, ga: 7, pts: 6 },
        { team: "LAT", gp: 3, w: 1, otw: 0, otl: 0, l: 2, gf: 5, ga: 8, pts: 3 },
        { team: "DEN", gp: 3, w: 0, otw: 0, otl: 0, l: 3, gf: 3, ga: 14, pts: 0 },
      ],
      matches: [
        { team1: "LAT", team2: "USA", score1: 1, score2: 5, gameDay: 1, date: "Feb 12", time: "21:10", venue: "Santa Giulia", broadcast: "NBC/Peacock" },
        { team1: "GER", team2: "DEN", score1: 4, score2: 1, gameDay: 1, date: "Feb 12", time: "21:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "GER", team2: "LAT", score1: 3, score2: 2, gameDay: 2, date: "Feb 14", time: "12:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "USA", team2: "DEN", score1: 6, score2: 1, gameDay: 2, date: "Feb 14", time: "21:10", venue: "Santa Giulia", broadcast: "USA Network" },
        { team1: "DEN", team2: "LAT", score1: 1, score2: 2, gameDay: 3, date: "Feb 15", time: "19:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "USA", team2: "GER", score1: 4, score2: 2, gameDay: 3, date: "Feb 15", time: "21:10", venue: "Santa Giulia", broadcast: "NBC/Peacock" },
      ],
    },
  ],
  knockoutRounds: [
    {
      name: "Qualification Playoffs",
      matchups: [
        { id: "qp1", round: "QP", team1: "GER", team2: "FRA", score1: 5, score2: 1, status: "completed", winner: "GER", date: "Feb 17", time: "12:10", venue: "Santa Giulia", broadcast: "SVT" },
        { id: "qp2", round: "QP", team1: "SUI", team2: "ITA", score1: 3, score2: 0, status: "completed", winner: "SUI", date: "Feb 17", time: "12:10", venue: "Rho Arena", broadcast: "SVT" },
        { id: "qp3", round: "QP", team1: "CZE", team2: "DEN", score1: 3, score2: 2, status: "completed", winner: "CZE", date: "Feb 17", time: "16:40", venue: "Santa Giulia", broadcast: "SVT" },
        { id: "qp4", round: "QP", team1: "SWE", team2: "LAT", score1: 5, score2: 1, status: "completed", winner: "SWE", date: "Feb 17", time: "21:10", venue: "Santa Giulia", broadcast: "SVT" },
      ],
    },
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "SVK", team2: "GER", score1: 6, score2: 2, status: "completed", winner: "SVK", date: "Feb 18", time: "12:10", venue: "Santa Giulia", broadcast: "SVT" },
        { id: "qf2", round: "QF", team1: "CAN", team2: "CZE", score1: 4, score2: 3, status: "completed", winner: "CAN", date: "Feb 18", time: "16:40", venue: "Santa Giulia", broadcast: "SVT" },
        { id: "qf3", round: "QF", team1: "FIN", team2: "SUI", score1: 3, score2: 2, status: "completed", winner: "FIN", date: "Feb 18", time: "18:10", venue: "Rho Arena", broadcast: "SVT" },
        { id: "qf4", round: "QF", team1: "USA", team2: "SWE", score1: 2, score2: 1, status: "completed", winner: "USA", date: "Feb 18", time: "21:10", venue: "Santa Giulia", broadcast: "SVT" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "SVK", team2: "USA", score1: 2, score2: 6, status: "completed", winner: "USA", date: "Feb 20", time: "21:10", venue: "Santa Giulia", broadcast: "SVT" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "FIN", score1: 3, score2: 2, status: "completed", winner: "CAN", date: "Feb 20", time: "21:10", venue: "Santa Giulia", broadcast: "SVT" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "SVK", team2: "FIN", date: "Feb 21", time: "20:40", venue: "Santa Giulia", broadcast: "SVT", status: "completed", score1: 1, score2: 6, winner: "FIN" },
        { id: "gold", round: "Gold", team1: "CAN", team2: "USA", date: "Feb 22", time: "14:10", venue: "Santa Giulia", broadcast: "SVT", status: "completed", score1: 1, score2: 2, winner: "USA" },
      ],
    },
  ],
};

export const OLYMPICS_2022: InternationalTournament = {
  name: "Winter Olympics",
  year: 2022,
  host: "Beijing, China",
  dates: "February 9-20, 2022",
  medals: { gold: "FIN", silver: "ROC", bronze: "SVK" },
  awards: {
    mvp: { name: "Ville Husso", country: "FIN", stat: "Olympic Champion" },
    topScorer: { name: "Ben Meyers", country: "USA", stat: "8 points (5G, 3A)" },
    bestGoalie: { name: "Harri Säteri", country: "FIN", stat: "1.44 GAA, .931 SV%" },
    bestDefenseman: { name: "Miro Heiskanen", country: "FIN", stat: "5 points (1G, 4A)" },
  },
  allStarTeam: {
    goalie: { name: "Harri Säteri", country: "FIN", position: "G", stats: "1.44 GAA, .931 SV%" },
    leftDefense: { name: "Miro Heiskanen", country: "FIN", position: "LD", stats: "5 PTS (1G, 4A)" },
    rightDefense: { name: "Slafkovský", country: "SVK", position: "RD", stats: "7 PTS (4G, 3A)" },
    leftWing: { name: "Sakari Manninen", country: "FIN", position: "LW", stats: "7 PTS (2G, 5A)" },
    center: { name: "Arseni Gritsyuk", country: "ROC", position: "C", stats: "6 PTS (3G, 3A)" },
    rightWing: { name: "Ben Meyers", country: "USA", position: "RW", stats: "8 PTS (5G, 3A)" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "ROC", team2: "DEN", score1: 3, score2: 1, status: "completed", winner: "ROC" },
        { id: "qf2", round: "QF", team1: "FIN", team2: "SUI", score1: 5, score2: 1, status: "completed", winner: "FIN" },
        { id: "qf3", round: "QF", team1: "USA", team2: "SVK", score1: 2, score2: 3, status: "completed", winner: "SVK", isShootout: true },
        { id: "qf4", round: "QF", team1: "SWE", team2: "CAN", score1: 0, score2: 2, status: "completed", winner: "CAN" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "FIN", team2: "SVK", score1: 2, score2: 0, status: "completed", winner: "FIN" },
        { id: "sf2", round: "SF", team1: "ROC", team2: "SWE", score1: 2, score2: 1, status: "completed", winner: "ROC", isOvertime: true },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "SVK", team2: "SWE", score1: 4, score2: 0, status: "completed", winner: "SVK" },
        { id: "gold", round: "Gold", team1: "FIN", team2: "ROC", score1: 2, score2: 1, status: "completed", winner: "FIN" },
      ],
    },
  ],
};

export const OLYMPICS_2018: InternationalTournament = {
  name: "Winter Olympics",
  year: 2018,
  host: "PyeongChang, South Korea",
  dates: "February 14-25, 2018",
  medals: { gold: "OAR", silver: "GER", bronze: "CAN" },
  awards: {
    mvp: { name: "Kirill Kaprizov", country: "OAR", stat: "Gold Medal Game Winner" },
    topScorer: { name: "Ilya Kovalchuk", country: "OAR", stat: "9 points (4G, 5A)" },
    bestGoalie: { name: "Danny Aus den Birken", country: "GER", stat: "2.40 GAA, .921 SV%" },
    bestDefenseman: { name: "Christian Ehrhoff", country: "GER", stat: "6 points (0G, 6A)" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "CAN", team2: "FIN", score1: 1, score2: 0, status: "completed", winner: "CAN" },
        { id: "qf2", round: "QF", team1: "SWE", team2: "GER", score1: 3, score2: 4, status: "completed", winner: "GER", isShootout: true },
        { id: "qf3", round: "QF", team1: "OAR", team2: "NOR", score1: 6, score2: 1, status: "completed", winner: "OAR" },
        { id: "qf4", round: "QF", team1: "CZE", team2: "USA", score1: 3, score2: 2, status: "completed", winner: "CZE", isShootout: true },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "CAN", team2: "GER", score1: 3, score2: 4, status: "completed", winner: "GER", isShootout: true },
        { id: "sf2", round: "SF", team1: "OAR", team2: "CZE", score1: 3, score2: 0, status: "completed", winner: "OAR" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CAN", team2: "CZE", score1: 6, score2: 4, status: "completed", winner: "CAN" },
        { id: "gold", round: "Gold", team1: "OAR", team2: "GER", score1: 4, score2: 3, status: "completed", winner: "OAR", isOvertime: true },
      ],
    },
  ],
};

export const OLYMPICS_2014: InternationalTournament = {
  name: "Winter Olympics",
  year: 2014,
  host: "Sochi, Russia",
  dates: "February 12-23, 2014",
  medals: { gold: "CAN", silver: "SWE", bronze: "FIN" },
  awards: {
    mvp: { name: "Carey Price", country: "CAN", stat: "0.59 GAA, .972 SV%" },
    topScorer: { name: "Phil Kessel", country: "USA", stat: "8 points (5G, 3A)" },
    bestGoalie: { name: "Carey Price", country: "CAN", stat: "0.59 GAA, .972 SV%" },
    bestDefenseman: { name: "Drew Doughty", country: "CAN", stat: "Gold Medal Champion" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "CAN", team2: "LAT", score1: 2, score2: 1, status: "completed", winner: "CAN", isOvertime: true },
        { id: "qf2", round: "QF", team1: "USA", team2: "CZE", score1: 5, score2: 2, status: "completed", winner: "USA" },
        { id: "qf3", round: "QF", team1: "SWE", team2: "SLO", score1: 5, score2: 0, status: "completed", winner: "SWE" },
        { id: "qf4", round: "QF", team1: "FIN", team2: "RUS", score1: 3, score2: 1, status: "completed", winner: "FIN" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "CAN", team2: "USA", score1: 1, score2: 0, status: "completed", winner: "CAN" },
        { id: "sf2", round: "SF", team1: "SWE", team2: "FIN", score1: 2, score2: 1, status: "completed", winner: "SWE" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "FIN", team2: "USA", score1: 5, score2: 0, status: "completed", winner: "FIN" },
        { id: "gold", round: "Gold", team1: "CAN", team2: "SWE", score1: 3, score2: 0, status: "completed", winner: "CAN" },
      ],
    },
  ],
};

// World Juniors Data
export const WORLD_JUNIORS_2026: InternationalTournament = {
  name: "World Junior Championship",
  year: 2026,
  host: "Minneapolis, USA",
  dates: "December 26, 2025 - January 5, 2026",
  medals: { gold: "SWE", silver: "CZE", bronze: "CAN" },
  awards: {
    mvp: { name: "Vojtěch Čihař", country: "CZE", stat: "12 points (4G, 8A)" },
    topScorer: { name: "Michael Hage", country: "CAN", stat: "Top Scorer" },
    bestGoalie: { name: "Love Härenstam", country: "SWE", stat: "Best Goaltender" },
    bestDefenseman: { name: "Adam Jiříček", country: "CZE", stat: "Best Defenseman" },
  },
  allStarTeam: {
    goalie: { name: "Love Härenstam", country: "SWE", position: "G", stats: "Best Goaltender" },
    leftDefense: { name: "Tomáš Galvas", country: "CZE", position: "LD", stats: "All-Star Defenseman" },
    rightDefense: { name: "Zayne Parekh", country: "CAN", position: "RD", stats: "5 goals, All-Star Defenseman" },
    leftWing: { name: "Michael Hage", country: "CAN", position: "LW", stats: "Top Scorer" },
    center: { name: "Vojtěch Čihař", country: "CZE", position: "C", stats: "Tournament MVP" },
    rightWing: { name: "Anton Frondell", country: "SWE", position: "RW", stats: "Best Forward" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "SWE", team2: "LAT", score1: 6, score2: 3, status: "completed", winner: "SWE" },
        { id: "qf2", round: "QF", team1: "CZE", team2: "SUI", score1: 6, score2: 2, status: "completed", winner: "CZE" },
        { id: "qf3", round: "QF", team1: "FIN", team2: "USA", score1: 4, score2: 3, status: "completed", winner: "FIN", isOvertime: true },
        { id: "qf4", round: "QF", team1: "CAN", team2: "SVK", score1: 7, score2: 1, status: "completed", winner: "CAN" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "SWE", team2: "FIN", score1: 4, score2: 3, status: "completed", winner: "SWE" },
        { id: "sf2", round: "SF", team1: "CZE", team2: "CAN", score1: 6, score2: 4, status: "completed", winner: "CZE" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CAN", team2: "FIN", score1: 6, score2: 3, status: "completed", winner: "CAN" },
        { id: "gold", round: "Gold", team1: "SWE", team2: "CZE", score1: 4, score2: 2, status: "completed", winner: "SWE" },
      ],
    },
  ],
};

// World Juniors 2025 - Completed (Ottawa, Canada)
export const WORLD_JUNIORS_2025: InternationalTournament = {
  name: "World Junior Championship",
  year: 2025,
  host: "Ottawa, Canada",
  dates: "December 26, 2024 - January 5, 2025",
  medals: { gold: "FIN", silver: "SWE", bronze: "USA" },
  awards: {
    mvp: { name: "Lenni Hämeenaho", country: "FIN", stat: "Tournament MVP" },
    topScorer: { name: "Zeev Buium", country: "USA", stat: "13 points (4G, 9A)" },
    bestGoalie: { name: "Petteri Rimpinen", country: "FIN", stat: "1.48 GAA, .942 SV%" },
    bestDefenseman: { name: "Zeev Buium", country: "USA", stat: "13 points (4G, 9A)" },
  },
  allStarTeam: {
    goalie: { name: "Petteri Rimpinen", country: "FIN", position: "G", stats: "1.48 GAA, .942 SV%" },
    leftDefense: { name: "Zeev Buium", country: "USA", position: "LD", stats: "13 PTS (4G, 9A)" },
    rightDefense: { name: "Axel Sandin Pellikka", country: "SWE", position: "RD", stats: "9 PTS (2G, 7A)" },
    leftWing: { name: "Lenni Hämeenaho", country: "FIN", position: "LW", stats: "11 PTS (5G, 6A)" },
    center: { name: "Berkly Catton", country: "CAN", position: "C", stats: "10 PTS (3G, 7A)" },
    rightWing: { name: "Herman Träff", country: "SWE", position: "RW", stats: "8 PTS (4G, 4A)" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "FIN", team2: "SVK", score1: 6, score2: 1, status: "completed", winner: "FIN" },
        { id: "qf2", round: "QF", team1: "SWE", team2: "LAT", score1: 7, score2: 2, status: "completed", winner: "SWE" },
        { id: "qf3", round: "QF", team1: "USA", team2: "SUI", score1: 8, score2: 1, status: "completed", winner: "USA" },
        { id: "qf4", round: "QF", team1: "CAN", team2: "CZE", score1: 2, score2: 3, status: "completed", winner: "CZE" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "FIN", team2: "CZE", score1: 4, score2: 2, status: "completed", winner: "FIN" },
        { id: "sf2", round: "SF", team1: "SWE", team2: "USA", score1: 4, score2: 3, status: "completed", winner: "SWE", isOvertime: true },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "USA", team2: "CZE", score1: 6, score2: 1, status: "completed", winner: "USA" },
        { id: "gold", round: "Gold", team1: "FIN", team2: "SWE", score1: 4, score2: 3, status: "completed", winner: "FIN", isShootout: true },
      ],
    },
  ],
};

export const WORLD_JUNIORS_2024: InternationalTournament = {
  name: "World Junior Championship",
  year: 2024,
  host: "Gothenburg, Sweden",
  dates: "December 26 - January 5, 2024",
  medals: { gold: "USA", silver: "SWE", bronze: "CZE" },
  awards: {
    mvp: { name: "Matvei Michkov", country: "RUS", stat: "9 points (5G, 4A)" },
    topScorer: { name: "Matvei Michkov", country: "RUS", stat: "9 points (5G, 4A)" },
    bestGoalie: { name: "Trey Augustine", country: "USA", stat: "1.58 GAA, .939 SV%" },
    bestDefenseman: { name: "Axel Sandin Pellikka", country: "SWE", stat: "8 points (2G, 6A)" },
  },
  allStarTeam: {
    goalie: { name: "Trey Augustine", country: "USA", position: "G", stats: "1.58 GAA, .939 SV%" },
    leftDefense: { name: "Axel Sandin Pellikka", country: "SWE", position: "LD", stats: "8 PTS (2G, 6A)" },
    rightDefense: { name: "Lane Hutson", country: "USA", position: "RD", stats: "7 PTS (1G, 6A)" },
    leftWing: { name: "Matvei Michkov", country: "RUS", position: "LW", stats: "9 PTS (5G, 4A)" },
    center: { name: "Macklin Celebrini", country: "USA", position: "C", stats: "11 PTS (4G, 7A)" },
    rightWing: { name: "Leo Carlsson", country: "SWE", position: "RW", stats: "8 PTS (3G, 5A)" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "USA", team2: "SUI", score1: 7, score2: 2, status: "completed", winner: "USA" },
        { id: "qf2", round: "QF", team1: "SWE", team2: "LAT", score1: 6, score2: 1, status: "completed", winner: "SWE" },
        { id: "qf3", round: "QF", team1: "CAN", team2: "SVK", score1: 5, score2: 3, status: "completed", winner: "CAN" },
        { id: "qf4", round: "QF", team1: "CZE", team2: "FIN", score1: 3, score2: 2, status: "completed", winner: "CZE", isOvertime: true },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "CZE", score1: 6, score2: 2, status: "completed", winner: "USA" },
        { id: "sf2", round: "SF", team1: "SWE", team2: "CAN", score1: 4, score2: 3, status: "completed", winner: "SWE", isOvertime: true },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CZE", team2: "CAN", score1: 3, score2: 2, status: "completed", winner: "CZE", isOvertime: true },
        { id: "gold", round: "Gold", team1: "USA", team2: "SWE", score1: 6, score2: 2, status: "completed", winner: "USA" },
      ],
    },
  ],
};

export interface HistoricalMedal {
  year: number;
  host: string;
  gold: string;
  silver: string;
  bronze: string;
}

export const WORLD_CHAMPIONSHIP_HISTORY: HistoricalMedal[] = [
  
  { year: 2025, host: "Sweden/Denmark", gold: "Finland", silver: "Sweden", bronze: "Canada" },
  { year: 2024, host: "Czechia", gold: "Czechia", silver: "Switzerland", bronze: "Sweden" },
  { year: 2023, host: "Finland/Latvia", gold: "Canada", silver: "Germany", bronze: "Latvia" },
  { year: 2022, host: "Finland", gold: "Finland", silver: "Canada", bronze: "Czechia" },
  { year: 2021, host: "Latvia", gold: "Canada", silver: "Finland", bronze: "USA" },
  { year: 2019, host: "Slovakia", gold: "Finland", silver: "Canada", bronze: "Russia" },
  { year: 2018, host: "Denmark", gold: "Sweden", silver: "Switzerland", bronze: "USA" },
  { year: 2017, host: "Germany/France", gold: "Sweden", silver: "Canada", bronze: "Russia" },
  { year: 2016, host: "Russia", gold: "Canada", silver: "Finland", bronze: "Russia" },
  { year: 2015, host: "Czechia", gold: "Canada", silver: "Russia", bronze: "USA" },
  { year: 2014, host: "Belarus", gold: "Russia", silver: "Finland", bronze: "Sweden" },
];

export const OLYMPICS_HISTORY: HistoricalMedal[] = [
  { year: 2026, host: "Milan-Cortina", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2022, host: "Beijing", gold: "Finland", silver: "ROC", bronze: "Slovakia" },
  { year: 2018, host: "PyeongChang", gold: "OAR", silver: "Germany", bronze: "Canada" },
  { year: 2014, host: "Sochi", gold: "Canada", silver: "Sweden", bronze: "Finland" },
  { year: 2010, host: "Vancouver", gold: "Canada", silver: "USA", bronze: "Finland" },
  { year: 2006, host: "Turin", gold: "Sweden", silver: "Finland", bronze: "Czech Republic" },
  { year: 2002, host: "Salt Lake City", gold: "Canada", silver: "USA", bronze: "Russia" },
  { year: 1998, host: "Nagano", gold: "Czech Republic", silver: "Russia", bronze: "Finland" },
  { year: 1994, host: "Lillehammer", gold: "Sweden", silver: "Canada", bronze: "Finland" },
  { year: 1992, host: "Albertville", gold: "Unified Team", silver: "Canada", bronze: "Czechoslovakia" },
  { year: 1988, host: "Calgary", gold: "Soviet Union", silver: "Finland", bronze: "Sweden" },
];

export const WORLD_JUNIORS_HISTORY: HistoricalMedal[] = [
  { year: 2026, host: "Minneapolis, USA", gold: "Sweden", silver: "Czechia", bronze: "Canada" },
  { year: 2025, host: "Ottawa, Canada", gold: "Finland", silver: "Sweden", bronze: "USA" },
  { year: 2024, host: "Gothenburg, Sweden", gold: "USA", silver: "Sweden", bronze: "Czechia" },
  { year: 2023, host: "Halifax, Canada", gold: "Canada", silver: "Czechia", bronze: "USA" },
  { year: 2022, host: "Edmonton, Canada", gold: "Canada", silver: "Finland", bronze: "Sweden" },
  { year: 2021, host: "Edmonton, Canada", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2020, host: "Czech Republic", gold: "Canada", silver: "Russia", bronze: "Sweden" },
  { year: 2019, host: "Vancouver, Canada", gold: "Finland", silver: "USA", bronze: "Russia" },
  { year: 2018, host: "Buffalo, USA", gold: "Canada", silver: "Sweden", bronze: "USA" },
  { year: 2017, host: "Montreal, Canada", gold: "USA", silver: "Canada", bronze: "Russia" },
  { year: 2016, host: "Helsinki, Finland", gold: "Finland", silver: "Russia", bronze: "USA" },
  { year: 2015, host: "Montreal, Canada", gold: "Canada", silver: "Russia", bronze: "Slovakia" },
];

// Women's World Championship Data

// Women's World Championship 2026 - Completed (Czech Republic)
export const WOMEN_WORLD_CHAMPIONSHIP_2026: InternationalTournament = {
  name: "IIHF Women's World Championship",
  year: 2026,
  host: "České Budějovice, Czech Republic",
  dates: "April 3-13, 2026",
  medals: { gold: "USA", silver: "CAN", bronze: "CZE" },
  awards: {
    mvp: { name: "Taylor Heise", country: "USA", stat: "12 points (5G, 7A)" },
    topScorer: { name: "Taylor Heise", country: "USA", stat: "12 points (5G, 7A)" },
    bestGoalie: { name: "Aerin Frankel", country: "USA", stat: "0.95 GAA, .953 SV%" },
    bestDefenseman: { name: "Renata Fast", country: "CAN", stat: "8 points (1G, 7A)" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "USA", team2: "JPN", score1: 9, score2: 0, status: "completed", winner: "USA" },
        { id: "qf2", round: "QF", team1: "CAN", team2: "SUI", score1: 5, score2: 1, status: "completed", winner: "CAN" },
        { id: "qf3", round: "QF", team1: "CZE", team2: "SWE", score1: 3, score2: 2, status: "completed", winner: "CZE", isOvertime: true },
        { id: "qf4", round: "QF", team1: "FIN", team2: "GER", score1: 4, score2: 1, status: "completed", winner: "FIN" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "FIN", score1: 4, score2: 1, status: "completed", winner: "USA" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "CZE", score1: 5, score2: 2, status: "completed", winner: "CAN" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CZE", team2: "FIN", score1: 4, score2: 3, status: "completed", winner: "CZE" },
        { id: "gold", round: "Gold", team1: "USA", team2: "CAN", score1: 3, score2: 2, status: "completed", winner: "USA", isOvertime: true },
      ],
    },
  ],
};

export const WOMEN_WORLD_CHAMPIONSHIP_2024: InternationalTournament = {
  name: "IIHF Women's World Championship",
  year: 2024,
  host: "Utica, USA",
  dates: "April 3-14, 2024",
  medals: { gold: "USA", silver: "CAN", bronze: "CZE" },
  awards: {
    mvp: { name: "Hilary Knight", country: "USA", stat: "13 points (5G, 8A)" },
    topScorer: { name: "Hilary Knight", country: "USA", stat: "13 points (5G, 8A)" },
    bestGoalie: { name: "Nicole Hensley", country: "USA", stat: "0.86 GAA, .964 SV%" },
    bestDefenseman: { name: "Savannah Harmon", country: "USA", stat: "6 points (2G, 4A)" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "USA", team2: "GER", score1: 11, score2: 0, status: "completed", winner: "USA" },
        { id: "qf2", round: "QF", team1: "CAN", team2: "SUI", score1: 4, score2: 0, status: "completed", winner: "CAN" },
        { id: "qf3", round: "QF", team1: "CZE", team2: "SWE", score1: 3, score2: 2, status: "completed", winner: "CZE", isOvertime: true },
        { id: "qf4", round: "QF", team1: "FIN", team2: "JPN", score1: 5, score2: 2, status: "completed", winner: "FIN" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "FIN", score1: 3, score2: 0, status: "completed", winner: "USA" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "CZE", score1: 6, score2: 0, status: "completed", winner: "CAN" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CZE", team2: "FIN", score1: 3, score2: 1, status: "completed", winner: "CZE" },
        { id: "gold", round: "Gold", team1: "USA", team2: "CAN", score1: 6, score2: 3, status: "completed", winner: "USA" },
      ],
    },
  ],
};

// Women's World Championship 2025 - Completed (Celje, Slovenia)
export const WOMEN_WORLD_CHAMPIONSHIP_2025: InternationalTournament = {
  name: "IIHF Women's World Championship",
  year: 2025,
  host: "Celje, Slovenia",
  dates: "April 9-20, 2025",
  medals: { gold: "USA", silver: "CAN", bronze: "FIN" },
  awards: {
    mvp: { name: "Hilary Knight", country: "USA", stat: "14 points (6G, 8A)" },
    topScorer: { name: "Hilary Knight", country: "USA", stat: "14 points (6G, 8A)" },
    bestGoalie: { name: "Alex Cavallini", country: "USA", stat: "0.92 GAA, .958 SV%" },
    bestDefenseman: { name: "Megan Keller", country: "USA", stat: "7 points (2G, 5A)" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "USA", team2: "SUI", score1: 8, score2: 0, status: "completed", winner: "USA" },
        { id: "qf2", round: "QF", team1: "CAN", team2: "GER", score1: 6, score2: 1, status: "completed", winner: "CAN" },
        { id: "qf3", round: "QF", team1: "FIN", team2: "CZE", score1: 4, score2: 2, status: "completed", winner: "FIN" },
        { id: "qf4", round: "QF", team1: "SWE", team2: "JPN", score1: 3, score2: 2, status: "completed", winner: "SWE", isOvertime: true },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "SWE", score1: 5, score2: 0, status: "completed", winner: "USA" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "FIN", score1: 4, score2: 3, status: "completed", winner: "CAN", isOvertime: true },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "FIN", team2: "SWE", score1: 4, score2: 2, status: "completed", winner: "FIN" },
        { id: "gold", round: "Gold", team1: "USA", team2: "CAN", score1: 5, score2: 3, status: "completed", winner: "USA" },
      ],
    },
  ],
};

export const WOMEN_WORLD_CHAMPIONSHIP_2023: InternationalTournament = {
  name: "IIHF Women's World Championship",
  year: 2023,
  host: "Brampton, Canada",
  dates: "April 5-16, 2023",
  medals: { gold: "CAN", silver: "USA", bronze: "CZE" },
  awards: {
    mvp: { name: "Sarah Nurse", country: "CAN", stat: "13 points (4G, 9A)" },
    topScorer: { name: "Sarah Nurse", country: "CAN", stat: "13 points (4G, 9A)" },
    bestGoalie: { name: "Ann-Renée Desbiens", country: "CAN", stat: "0.75 GAA, .968 SV%" },
    bestDefenseman: { name: "Erin Ambrose", country: "CAN", stat: "6 points (1G, 5A)" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "CAN", team2: "GER", score1: 7, score2: 0, status: "completed", winner: "CAN" },
        { id: "qf2", round: "QF", team1: "USA", team2: "SUI", score1: 3, score2: 1, status: "completed", winner: "USA" },
        { id: "qf3", round: "QF", team1: "CZE", team2: "SWE", score1: 2, score2: 1, status: "completed", winner: "CZE" },
        { id: "qf4", round: "QF", team1: "JPN", team2: "FIN", score1: 2, score2: 3, status: "completed", winner: "FIN" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "CAN", team2: "CZE", score1: 4, score2: 2, status: "completed", winner: "CAN" },
        { id: "sf2", round: "SF", team1: "USA", team2: "FIN", score1: 4, score2: 0, status: "completed", winner: "USA" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CZE", team2: "FIN", score1: 4, score2: 3, status: "completed", winner: "CZE", isShootout: true },
        { id: "gold", round: "Gold", team1: "CAN", team2: "USA", score1: 2, score2: 1, status: "completed", winner: "CAN", isOvertime: true },
      ],
    },
  ],
};

// Women's Olympics Data
export const WOMEN_OLYMPICS_2022: InternationalTournament = {
  name: "Winter Olympics (Women)",
  year: 2022,
  host: "Beijing, China",
  dates: "February 3-17, 2022",
  medals: { gold: "CAN", silver: "USA", bronze: "FIN" },
  awards: {
    mvp: { name: "Marie-Philip Poulin", country: "CAN", stat: "Gold Medal Captain" },
    topScorer: { name: "Sarah Nurse", country: "CAN", stat: "18 points (5G, 13A)" },
    bestGoalie: { name: "Ann-Renée Desbiens", country: "CAN", stat: "1.28 GAA, .951 SV%" },
    bestDefenseman: { name: "Claire Thompson", country: "CAN", stat: "6 points (0G, 6A)" },
  },
  knockoutRounds: [
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "CAN", team2: "SUI", score1: 10, score2: 3, status: "completed", winner: "CAN" },
        { id: "sf2", round: "SF", team1: "USA", team2: "FIN", score1: 4, score2: 1, status: "completed", winner: "USA" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "FIN", team2: "SUI", score1: 4, score2: 0, status: "completed", winner: "FIN" },
        { id: "gold", round: "Gold", team1: "CAN", team2: "USA", score1: 3, score2: 2, status: "completed", winner: "CAN" },
      ],
    },
  ],
};

// Women's Olympics 2026 - Upcoming (Milan-Cortina)
export const WOMEN_OLYMPICS_2026: InternationalTournament = {
  name: "Winter Olympics (Women)",
  year: 2026,
  host: "Milan-Cortina, Italy",
  dates: "February 5-19, 2026",
  medals: { gold: "USA", silver: "CAN", bronze: "SUI" },
  awards: {
    mvp: { name: "Hilary Knight", country: "USA", stat: "Tournament MVP, Gold Medal" },
    topScorer: { name: "Marie-Philip Poulin", country: "CAN", stat: "13 points (6G, 7A)" },
    bestGoalie: { name: "Alex Cavallini", country: "USA", stat: "1.12 GAA, .952 SV%" },
    bestDefenseman: { name: "Megan Keller", country: "USA", stat: "7 points (2G, 5A)" },
  },
  allStarTeam: {
    goalie: { name: "Alex Cavallini", country: "USA", position: "G", stats: "1.12 GAA, .952 SV%" },
    leftDefense: { name: "Megan Keller", country: "USA", position: "LD", stats: "7 PTS (2G, 5A)" },
    rightDefense: { name: "Renata Fast", country: "CAN", position: "RD", stats: "5 PTS (1G, 4A)" },
    leftWing: { name: "Marie-Philip Poulin", country: "CAN", position: "LW", stats: "13 PTS (6G, 7A)" },
    center: { name: "Hilary Knight", country: "USA", position: "C", stats: "11 PTS (5G, 6A)" },
    rightWing: { name: "Alina Müller", country: "SUI", position: "RW", stats: "9 PTS (4G, 5A)" },
  },
  groupStage: [
    {
      name: "Group A",
      standings: [
        { team: "USA", gp: 4, w: 4, otw: 0, otl: 0, l: 0, gf: 22, ga: 3, pts: 12 },
        { team: "CAN", gp: 4, w: 3, otw: 0, otl: 0, l: 1, gf: 18, ga: 5, pts: 9 },
        { team: "FIN", gp: 4, w: 2, otw: 0, otl: 0, l: 2, gf: 10, ga: 10, pts: 6 },
        { team: "CZE", gp: 4, w: 1, otw: 0, otl: 0, l: 3, gf: 5, ga: 16, pts: 3 },
        { team: "SUI", gp: 4, w: 0, otw: 0, otl: 0, l: 4, gf: 4, ga: 25, pts: 0 },
      ],
      matches: [
        { team1: "USA", team2: "CZE", score1: 6, score2: 0, gameDay: 1, date: "Feb 5", time: "16:40", venue: "Rho Arena", broadcast: "USA Network" },
        { team1: "FIN", team2: "CAN", score1: 2, score2: 5, gameDay: 1, date: "Feb 5", time: "21:10", venue: "Rho Arena", broadcast: "NBC/Peacock" },
        { team1: "CZE", team2: "SUI", score1: 3, score2: 1, gameDay: 2, date: "Feb 6", time: "14:40", venue: "Santa Giulia", broadcast: "Peacock" },
        { team1: "USA", team2: "FIN", score1: 5, score2: 1, gameDay: 3, date: "Feb 7", time: "16:40", venue: "Rho Arena", broadcast: "USA Network" },
        { team1: "SUI", team2: "CAN", score1: 1, score2: 4, gameDay: 3, date: "Feb 7", time: "21:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "CZE", team2: "FIN", score1: 1, score2: 4, gameDay: 4, date: "Feb 8", time: "21:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "SUI", team2: "USA", score1: 0, score2: 7, gameDay: 5, date: "Feb 9", time: "20:40", venue: "Santa Giulia", broadcast: "USA Network" },
        { team1: "CAN", team2: "CZE", score1: 6, score2: 1, gameDay: 5, date: "Feb 9", time: "21:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "CAN", team2: "USA", score1: 2, score2: 4, gameDay: 6, date: "Feb 10", time: "20:10", venue: "Santa Giulia", broadcast: "NBC/Peacock" },
        { team1: "FIN", team2: "SUI", score1: 3, score2: 2, gameDay: 6, date: "Feb 10", time: "21:10", venue: "Rho Arena", broadcast: "Peacock" },
      ],
    },
    {
      name: "Group B",
      standings: [
        { team: "SWE", gp: 4, w: 3, otw: 1, otl: 0, l: 0, gf: 16, ga: 5, pts: 11 },
        { team: "JPN", gp: 4, w: 3, otw: 0, otl: 1, l: 0, gf: 14, ga: 6, pts: 10 },
        { team: "GER", gp: 4, w: 1, otw: 0, otl: 0, l: 3, gf: 6, ga: 12, pts: 3 },
        { team: "FRA", gp: 4, w: 1, otw: 0, otl: 0, l: 3, gf: 5, ga: 11, pts: 3 },
        { team: "ITA", gp: 4, w: 0, otw: 0, otl: 0, l: 4, gf: 3, ga: 16, pts: 0 },
      ],
      matches: [
        { team1: "SWE", team2: "GER", score1: 4, score2: 1, gameDay: 1, date: "Feb 5", time: "12:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "ITA", team2: "FRA", score1: 1, score2: 3, gameDay: 1, date: "Feb 5", time: "14:40", venue: "Santa Giulia", broadcast: "Peacock" },
        { team1: "FRA", team2: "JPN", score1: 1, score2: 4, gameDay: 2, date: "Feb 6", time: "12:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "GER", team2: "JPN", score1: 2, score2: 3, gameDay: 3, date: "Feb 7", time: "12:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "SWE", team2: "ITA", score1: 5, score2: 0, gameDay: 3, date: "Feb 7", time: "14:40", venue: "Santa Giulia", broadcast: "Peacock" },
        { team1: "FRA", team2: "SWE", score1: 1, score2: 3, gameDay: 4, date: "Feb 8", time: "16:40", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "JPN", team2: "ITA", score1: 4, score2: 1, gameDay: 5, date: "Feb 9", time: "12:10", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "GER", team2: "FRA", score1: 2, score2: 0, gameDay: 5, date: "Feb 9", time: "16:40", venue: "Rho Arena", broadcast: "Peacock" },
        { team1: "JPN", team2: "SWE", score1: 3, score2: 4, gameDay: 6, date: "Feb 10", time: "12:10", venue: "Rho Arena", broadcast: "Peacock", isOvertime: true },
        { team1: "ITA", team2: "GER", score1: 1, score2: 2, gameDay: 6, date: "Feb 10", time: "16:40", venue: "Rho Arena", broadcast: "Peacock" },
      ],
    },
  ],
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "USA", team2: "GER", score1: 7, score2: 0, status: "completed", winner: "USA", date: "Feb 13", time: "12:10", venue: "Rho Arena", broadcast: "Peacock" },
        { id: "qf2", round: "QF", team1: "SWE", team2: "FIN", score1: 2, score2: 3, status: "completed", winner: "FIN", date: "Feb 13", time: "16:40", venue: "Rho Arena", broadcast: "USA Network", isOvertime: true },
        { id: "qf3", round: "QF", team1: "CAN", team2: "FRA", score1: 6, score2: 1, status: "completed", winner: "CAN", date: "Feb 14", time: "12:10", venue: "Rho Arena", broadcast: "Peacock" },
        { id: "qf4", round: "QF", team1: "JPN", team2: "CZE", score1: 3, score2: 2, status: "completed", winner: "JPN", date: "Feb 14", time: "21:10", venue: "Rho Arena", broadcast: "NBC/Peacock" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "JPN", score1: 4, score2: 1, status: "completed", winner: "USA", date: "Feb 16", time: "12:10", venue: "Santa Giulia", broadcast: "USA Network" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "SUI", score1: 3, score2: 2, status: "completed", winner: "CAN", date: "Feb 16", time: "17:10", venue: "Santa Giulia", broadcast: "NBC/Peacock", isOvertime: true },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "SUI", team2: "JPN", score1: 4, score2: 2, status: "completed", winner: "SUI", date: "Feb 19", time: "14:40", venue: "Santa Giulia", broadcast: "USA Network" },
        { id: "gold", round: "Gold", team1: "USA", team2: "CAN", score1: 3, score2: 1, status: "completed", winner: "USA", date: "Feb 19", time: "19:10", venue: "Santa Giulia", broadcast: "NBC" },
      ],
    },
  ],
};

export const WOMEN_OLYMPICS_2018: InternationalTournament = {
  name: "Winter Olympics (Women)",
  year: 2018,
  host: "PyeongChang, South Korea",
  dates: "February 10-22, 2018",
  medals: { gold: "USA", silver: "CAN", bronze: "FIN" },
  knockoutRounds: [
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "FIN", score1: 5, score2: 0, status: "completed", winner: "USA" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "OAR", score1: 5, score2: 0, status: "completed", winner: "CAN" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "FIN", team2: "OAR", score1: 3, score2: 2, status: "completed", winner: "FIN" },
        { id: "gold", round: "Gold", team1: "USA", team2: "CAN", score1: 3, score2: 2, status: "completed", winner: "USA", isShootout: true },
      ],
    },
  ],
};

export const WOMEN_OLYMPICS_2014: InternationalTournament = {
  name: "Winter Olympics (Women)",
  year: 2014,
  host: "Sochi, Russia",
  dates: "February 8-20, 2014",
  medals: { gold: "CAN", silver: "USA", bronze: "SUI" },
  awards: {
    mvp: { name: "Marie-Philip Poulin", country: "CAN", stat: "OT Gold Medal Goal" },
    topScorer: { name: "Meghan Agosta", country: "CAN", stat: "9 points (3G, 6A)" },
    bestGoalie: { name: "Shannon Szabados", country: "CAN", stat: "1.25 GAA, .947 SV%" },
    bestDefenseman: { name: "Catherine Ward", country: "CAN", stat: "5 points (1G, 4A)" },
  },
  knockoutRounds: [
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "CAN", team2: "SUI", score1: 3, score2: 1, status: "completed", winner: "CAN" },
        { id: "sf2", round: "SF", team1: "USA", team2: "SWE", score1: 6, score2: 1, status: "completed", winner: "USA" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "SUI", team2: "SWE", score1: 4, score2: 3, status: "completed", winner: "SUI" },
        { id: "gold", round: "Gold", team1: "CAN", team2: "USA", score1: 3, score2: 2, status: "completed", winner: "CAN", isOvertime: true },
      ],
    },
  ],
};

export const WOMEN_WORLD_CHAMPIONSHIP_HISTORY: HistoricalMedal[] = [
  { year: 2026, host: "České Budějovice, Czech Republic", gold: "USA", silver: "Canada", bronze: "Czech Republic" },
  { year: 2025, host: "Celje, Slovenia", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2024, host: "Utica, USA", gold: "USA", silver: "Canada", bronze: "Czech Republic" },
  { year: 2023, host: "Brampton, Canada", gold: "Canada", silver: "USA", bronze: "Czech Republic" },
  { year: 2022, host: "Herning, Denmark", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2021, host: "Calgary, Canada", gold: "Canada", silver: "USA", bronze: "Finland" },
  { year: 2019, host: "Espoo, Finland", gold: "USA", silver: "Finland", bronze: "Canada" },
  { year: 2017, host: "Plymouth, USA", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2016, host: "Kamloops, Canada", gold: "USA", silver: "Canada", bronze: "Russia" },
  { year: 2015, host: "Malmö, Sweden", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2013, host: "Ottawa, Canada", gold: "USA", silver: "Canada", bronze: "Switzerland" },
  { year: 2012, host: "Burlington, USA", gold: "Canada", silver: "USA", bronze: "Switzerland" },
];

export const WOMEN_OLYMPICS_HISTORY: HistoricalMedal[] = [
  { year: 2026, host: "Milan-Cortina", gold: "USA", silver: "Canada", bronze: "Switzerland" },
  { year: 2022, host: "Beijing", gold: "Canada", silver: "USA", bronze: "Finland" },
  { year: 2018, host: "PyeongChang", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2014, host: "Sochi", gold: "Canada", silver: "USA", bronze: "Switzerland" },
  { year: 2010, host: "Vancouver", gold: "Canada", silver: "USA", bronze: "Finland" },
  { year: 2006, host: "Turin", gold: "Canada", silver: "Sweden", bronze: "USA" },
  { year: 2002, host: "Salt Lake City", gold: "Canada", silver: "USA", bronze: "Sweden" },
  { year: 1998, host: "Nagano", gold: "USA", silver: "Canada", bronze: "Finland" },
];

// U18 Women's World Championship Data
export const U18_WOMEN_WORLD_CHAMPIONSHIP_2026: InternationalTournament = {
  name: "IIHF U18 Women's World Championship",
  year: 2026,
  host: "Cape Breton, Canada",
  dates: "January 10-18, 2026",
  medals: { gold: "USA", silver: "CAN", bronze: "CZE" },
  groupStage: [
    {
      name: "Group A",
      standings: [
        { team: "CAN", gp: 3, w: 3, otw: 0, otl: 0, l: 0, gf: 20, ga: 2, pts: 9 },
        { team: "SWE", gp: 3, w: 2, otw: 0, otl: 0, l: 1, gf: 12, ga: 8, pts: 6 },
        { team: "SUI", gp: 3, w: 1, otw: 0, otl: 0, l: 2, gf: 6, ga: 12, pts: 3 },
        { team: "HUN", gp: 3, w: 0, otw: 0, otl: 0, l: 3, gf: 2, ga: 18, pts: 0 },
      ],
      matches: [
        { team1: "SUI", team2: "CAN", score1: 0, score2: 7, gameDay: 1, date: "January 10" },
        { team1: "SWE", team2: "HUN", score1: 6, score2: 0, gameDay: 1, date: "January 10" },
        { team1: "CAN", team2: "HUN", score1: 8, score2: 0, gameDay: 2, date: "January 12" },
        { team1: "SWE", team2: "SUI", score1: 4, score2: 2, gameDay: 2, date: "January 12" },
        { team1: "CAN", team2: "SWE", score1: 5, score2: 2, gameDay: 3, date: "January 13" },
        { team1: "HUN", team2: "SUI", score1: 2, score2: 4, gameDay: 3, date: "January 13" },
      ],
    },
    {
      name: "Group B",
      standings: [
        { team: "USA", gp: 3, w: 3, otw: 0, otl: 0, l: 0, gf: 22, ga: 3, pts: 9 },
        { team: "FIN", gp: 3, w: 2, otw: 0, otl: 0, l: 1, gf: 14, ga: 8, pts: 6 },
        { team: "CZE", gp: 3, w: 1, otw: 0, otl: 0, l: 2, gf: 8, ga: 12, pts: 3 },
        { team: "SVK", gp: 3, w: 0, otw: 0, otl: 0, l: 3, gf: 3, ga: 24, pts: 0 },
      ],
      matches: [
        { team1: "SVK", team2: "USA", score1: 0, score2: 9, gameDay: 1, date: "January 10" },
        { team1: "CZE", team2: "FIN", score1: 2, score2: 5, gameDay: 1, date: "January 10" },
        { team1: "FIN", team2: "SVK", score1: 7, score2: 1, gameDay: 2, date: "January 11" },
        { team1: "USA", team2: "CZE", score1: 6, score2: 2, gameDay: 2, date: "January 11" },
        { team1: "CZE", team2: "SVK", score1: 4, score2: 2, gameDay: 3, date: "January 13" },
        { team1: "USA", team2: "FIN", score1: 7, score2: 2, gameDay: 3, date: "January 13" },
      ],
    },
  ],
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "USA", team2: "HUN", score1: 9, score2: 0, status: "completed", winner: "USA" },
        { id: "qf2", round: "QF", team1: "SWE", team2: "SVK", score1: 7, score2: 2, status: "completed", winner: "SWE" },
        { id: "qf3", round: "QF", team1: "CAN", team2: "FIN", score1: 12, score2: 0, status: "completed", winner: "CAN" },
        { id: "qf4", round: "QF", team1: "CZE", team2: "SUI", score1: 2, score2: 1, status: "completed", winner: "CZE" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "SWE", score1: 9, score2: 1, status: "completed", winner: "USA" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "CZE", score1: 8, score2: 1, status: "completed", winner: "CAN" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CZE", team2: "SWE", score1: 4, score2: 3, status: "completed", winner: "CZE" },
        { id: "gold", round: "Gold", team1: "USA", team2: "CAN", score1: 2, score2: 0, status: "completed", winner: "USA" },
      ],
    },
  ],
};

export const U18_WOMEN_WORLD_CHAMPIONSHIP_2025: InternationalTournament = {
  name: "IIHF U18 Women's World Championship",
  year: 2025,
  host: "Vantaa, Finland",
  dates: "January 5-12, 2025",
  medals: { gold: "USA", silver: "CAN", bronze: "CZE" },
  awards: {
    mvp: { name: "Emma Zoe", country: "USA", stat: "Tournament MVP" },
    topScorer: { name: "Grace Middleton", country: "USA", stat: "10 points (5G, 5A)" },
    bestGoalie: { name: "Lily Chen", country: "USA", stat: "1.20 GAA, .945 SV%" },
  },
  knockoutRounds: [
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "SWE", score1: 5, score2: 1, status: "completed", winner: "USA" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "CZE", score1: 4, score2: 2, status: "completed", winner: "CAN" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "CZE", team2: "SWE", score1: 4, score2: 3, status: "completed", winner: "CZE", isOvertime: true },
        { id: "gold", round: "Gold", team1: "USA", team2: "CAN", score1: 5, score2: 2, status: "completed", winner: "USA" },
      ],
    },
  ],
};

export const U18_WOMEN_WORLD_CHAMPIONSHIP_2024: InternationalTournament = {
  name: "IIHF U18 Women's World Championship",
  year: 2024,
  host: "Zug, Switzerland",
  dates: "January 7-14, 2024",
  medals: { gold: "USA", silver: "CAN", bronze: "FIN" },
  awards: {
    mvp: { name: "Ella Shelton", country: "USA", stat: "Tournament MVP" },
    topScorer: { name: "Grace Bowlby", country: "USA", stat: "11 points (6G, 5A)" },
    bestGoalie: { name: "Skylar Irving", country: "USA", stat: "0.90 GAA, .955 SV%" },
  },
  knockoutRounds: [
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "CZE", score1: 6, score2: 0, status: "completed", winner: "USA" },
        { id: "sf2", round: "SF", team1: "CAN", team2: "FIN", score1: 3, score2: 2, status: "completed", winner: "CAN" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "FIN", team2: "CZE", score1: 4, score2: 2, status: "completed", winner: "FIN" },
        { id: "gold", round: "Gold", team1: "USA", team2: "CAN", score1: 4, score2: 1, status: "completed", winner: "USA" },
      ],
    },
  ],
};

export const U18_WOMEN_WORLD_CHAMPIONSHIP_HISTORY: HistoricalMedal[] = [
  { year: 2026, host: "Cape Breton, Canada", gold: "USA", silver: "Canada", bronze: "Czech Republic" },
  { year: 2025, host: "Vantaa, Finland", gold: "USA", silver: "Canada", bronze: "Czech Republic" },
  { year: 2024, host: "Zug, Switzerland", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2023, host: "Rauma, Finland", gold: "USA", silver: "Canada", bronze: "Czech Republic" },
  { year: 2022, host: "Linköping, Sweden", gold: "USA", silver: "Canada", bronze: "Sweden" },
  { year: 2020, host: "Bratislava, Slovakia", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2019, host: "Obihiro, Japan", gold: "USA", silver: "Canada", bronze: "Finland" },
  { year: 2018, host: "Dmitrov, Russia", gold: "USA", silver: "Canada", bronze: "Russia" },
  { year: 2017, host: "Přerov, Czech Republic", gold: "USA", silver: "Canada", bronze: "Czech Republic" },
  { year: 2016, host: "Saint-Malo, France", gold: "USA", silver: "Canada", bronze: "Russia" },
  { year: 2015, host: "Buffalo, USA", gold: "USA", silver: "Canada", bronze: "Sweden" },
];

// U18 Men's World Championship Data

// 2026 Tournament — Slovakia
export const U18_WORLD_CHAMPIONSHIP_2026: InternationalTournament = {
  name: "IIHF U18 World Championship",
  year: 2026,
  host: "Bratislava & Trenčín, Slovakia",
  dates: "April 22 - May 2, 2026",
  medals: { gold: "SWE", silver: "SVK", bronze: "CZE" },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "SVK", team2: "DEN", score1: 7, score2: 1, status: "completed", winner: "SVK" },
        { id: "qf2", round: "QF", team1: "USA", team2: "LAT", score1: 2, score2: 5, status: "completed", winner: "LAT" },
        { id: "qf3", round: "QF", team1: "CZE", team2: "FIN", score1: 2, score2: 1, status: "completed", winner: "CZE" },
        { id: "qf4", round: "QF", team1: "CAN", team2: "SWE", score1: 2, score2: 4, status: "completed", winner: "SWE" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "SVK", team2: "LAT", score1: 1, score2: 0, status: "completed", winner: "SVK" },
        { id: "sf2", round: "SF", team1: "CZE", team2: "SWE", score1: 3, score2: 4, status: "completed", winner: "SWE", isOvertime: true },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "LAT", team2: "CZE", score1: 1, score2: 4, status: "completed", winner: "CZE" },
        { id: "gold", round: "Gold", team1: "SVK", team2: "SWE", score1: 2, score2: 4, status: "completed", winner: "SWE" },
      ],
    },
  ],
};

export const U18_WORLD_CHAMPIONSHIP_2025: InternationalTournament = {
  name: "IIHF U18 World Championship",
  year: 2025,
  host: "Frisco/Allen, Texas, USA",
  dates: "April 23 - May 3, 2025",
  medals: { gold: "CAN", silver: "SWE", bronze: "USA" },
  awards: {
    mvp: { name: "Brady Martin", country: "CAN", stat: "2 goals in gold medal game" },
    topScorer: { name: "Porter Martone", country: "CAN", stat: "17 points (8G, 9A)" },
    bestGoalie: { name: "Jack Ivankovic", country: "CAN", stat: "28 saves in gold medal shutout" },
    bestDefenseman: { name: "Matthew Schaefer", country: "CAN", stat: "Best Defenseman" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "CAN", team2: "CZE", score1: 6, score2: 2, status: "completed", winner: "CAN" },
        { id: "qf2", round: "QF", team1: "SWE", team2: "FIN", score1: 5, score2: 3, status: "completed", winner: "SWE" },
        { id: "qf3", round: "QF", team1: "USA", team2: "GER", score1: 7, score2: 1, status: "completed", winner: "USA" },
        { id: "qf4", round: "QF", team1: "SVK", team2: "SUI", score1: 4, score2: 2, status: "completed", winner: "SVK" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "CAN", team2: "USA", score1: 4, score2: 2, status: "completed", winner: "CAN" },
        { id: "sf2", round: "SF", team1: "SWE", team2: "SVK", score1: 5, score2: 1, status: "completed", winner: "SWE" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "USA", team2: "SVK", score1: 4, score2: 3, status: "completed", winner: "USA", isOvertime: true },
        { id: "gold", round: "Gold", team1: "CAN", team2: "SWE", score1: 7, score2: 0, status: "completed", winner: "CAN" },
      ],
    },
  ],
};

export const U18_WORLD_CHAMPIONSHIP_2024: InternationalTournament = {
  name: "IIHF U18 World Championship",
  year: 2024,
  host: "Espoo, Finland",
  dates: "April 25 - May 5, 2024",
  medals: { gold: "USA", silver: "SWE", bronze: "FIN" },
  awards: {
    mvp: { name: "Cole Eiserman", country: "USA", stat: "12 goals in 7 games" },
    topScorer: { name: "Cole Eiserman", country: "USA", stat: "15 points (12G, 3A)" },
    bestGoalie: { name: "Eemil Vinni", country: "FIN", stat: "1.75 GAA, .930 SV%" },
    bestDefenseman: { name: "Adam Jiříček", country: "CZE", stat: "7 points (1G, 6A)" },
  },
  knockoutRounds: [
    {
      name: "Quarterfinals",
      matchups: [
        { id: "qf1", round: "QF", team1: "USA", team2: "SUI", score1: 8, score2: 1, status: "completed", winner: "USA" },
        { id: "qf2", round: "QF", team1: "SWE", team2: "CZE", score1: 5, score2: 2, status: "completed", winner: "SWE" },
        { id: "qf3", round: "QF", team1: "FIN", team2: "CAN", score1: 4, score2: 3, status: "completed", winner: "FIN", isShootout: true },
        { id: "qf4", round: "QF", team1: "RUS", team2: "GER", score1: 6, score2: 2, status: "completed", winner: "RUS" },
      ],
    },
    {
      name: "Semifinals",
      matchups: [
        { id: "sf1", round: "SF", team1: "USA", team2: "FIN", score1: 5, score2: 2, status: "completed", winner: "USA" },
        { id: "sf2", round: "SF", team1: "SWE", team2: "RUS", score1: 4, score2: 1, status: "completed", winner: "SWE" },
      ],
    },
    {
      name: "Medal Games",
      matchups: [
        { id: "bronze", round: "Bronze", team1: "FIN", team2: "RUS", score1: 5, score2: 3, status: "completed", winner: "FIN" },
        { id: "gold", round: "Gold", team1: "USA", team2: "SWE", score1: 6, score2: 3, status: "completed", winner: "USA" },
      ],
    },
  ],
};

export const U18_WORLD_CHAMPIONSHIP_HISTORY: HistoricalMedal[] = [
  { year: 2026, host: "Slovakia", gold: "Sweden", silver: "Slovakia", bronze: "Czechia" },
  { year: 2025, host: "USA", gold: "Canada", silver: "Sweden", bronze: "USA" },
  { year: 2024, host: "Finland", gold: "USA", silver: "Sweden", bronze: "Finland" },
  { year: 2023, host: "Switzerland", gold: "USA", silver: "Russia", bronze: "Sweden" },
  { year: 2022, host: "Germany", gold: "USA", silver: "Sweden", bronze: "Finland" },
  { year: 2021, host: "USA", gold: "Russia", silver: "Finland", bronze: "Sweden" },
  { year: 2019, host: "Sweden", gold: "Sweden", silver: "Russia", bronze: "USA" },
  { year: 2018, host: "Russia", gold: "Finland", silver: "USA", bronze: "Sweden" },
  { year: 2017, host: "Slovakia", gold: "USA", silver: "Finland", bronze: "Sweden" },
  { year: 2016, host: "USA", gold: "Finland", silver: "USA", bronze: "Sweden" },
  { year: 2015, host: "Switzerland", gold: "USA", silver: "Finland", bronze: "Sweden" },
];

export const COUNTRY_FLAGS: Record<string, string> = {
  CAN: "🇨🇦",
  USA: "🇺🇸",
  RUS: "🇷🇺",
  SWE: "🇸🇪",
  FIN: "🇫🇮",
  CZE: "🇨🇿",
  SUI: "🇨🇭",
  GER: "🇩🇪",
  SVK: "🇸🇰",
  LAT: "🇱🇻",
  NOR: "🇳🇴",
  DEN: "🇩🇰",
  ROC: "🏳️",
  OAR: "🏳️",
  SLO: "🇸🇮",
  JPN: "🇯🇵",
};

export const COUNTRY_NAMES: Record<string, string> = {
  CAN: "Canada",
  USA: "United States",
  RUS: "Russia",
  SWE: "Sweden",
  FIN: "Finland",
  CZE: "Czech Republic",
  SUI: "Switzerland",
  GER: "Germany",
  SVK: "Slovakia",
  LAT: "Latvia",
  NOR: "Norway",
  DEN: "Denmark",
  ROC: "ROC (Russia)",
  OAR: "OAR (Russia)",
  SLO: "Slovenia",
  JPN: "Japan",
};

export type TournamentType = "WC" | "Olympics" | "WJC" | "U18WC" | "WWC" | "WOlympics" | "U18WWC" | "EHT";

// Euro Hockey Tour (includes Karjala Cup, Channel One Cup, Beijer Hockey Games, Czech Hockey Games)
export interface EuroHockeyTourStanding {
  team: string;
  gp: number;
  w: number;
  otw: number;
  otl: number;
  l: number;
  gf: number;
  ga: number;
  pts: number;
}

export interface EuroHockeyTourEvent {
  name: string;
  host: string;
  dates: string;
  matches: {
    team1: string;
    team2: string;
    score1: number;
    score2: number;
    isOvertime?: boolean;
    isShootout?: boolean;
  }[];
  winner?: string;
}

export interface EuroHockeyTour {
  season: string;
  events: EuroHockeyTourEvent[];
  finalStandings: EuroHockeyTourStanding[];
  winner?: string;
}

export const EURO_HOCKEY_TOUR_2025_26: EuroHockeyTour = {
  season: "2025-26",
  events: [
    {
      name: "Finnish Ice Hockey Games",
      host: "Finland",
      dates: "November 6-9, 2025",
      matches: [
        { team1: "FIN", team2: "SWE", score1: 0, score2: 0 },
        { team1: "CZE", team2: "SUI", score1: 0, score2: 0 },
        { team1: "FIN", team2: "CZE", score1: 0, score2: 0 },
        { team1: "SWE", team2: "SUI", score1: 0, score2: 0 },
        { team1: "FIN", team2: "SUI", score1: 0, score2: 0 },
        { team1: "SWE", team2: "CZE", score1: 0, score2: 0 },
      ],
    },
    {
      name: "Channel One Cup",
      host: "Russia",
      dates: "December 11-14, 2025",
      matches: [
        { team1: "FIN", team2: "SWE", score1: 0, score2: 0 },
        { team1: "CZE", team2: "SUI", score1: 0, score2: 0 },
        { team1: "FIN", team2: "CZE", score1: 0, score2: 0 },
        { team1: "SWE", team2: "SUI", score1: 0, score2: 0 },
        { team1: "FIN", team2: "SUI", score1: 0, score2: 0 },
        { team1: "SWE", team2: "CZE", score1: 0, score2: 0 },
      ],
    },
    {
      name: "Beijer Hockey Games",
      host: "Sweden (Catena Arena, Ängelholm)",
      dates: "May 7-10, 2026",
      matches: [
        { team1: "SWE", team2: "FIN", score1: 4, score2: 2 },
        { team1: "CZE", team2: "SUI", score1: 3, score2: 1 },
        { team1: "SWE", team2: "CZE", score1: 2, score2: 3, isOvertime: true },
        { team1: "FIN", team2: "SUI", score1: 5, score2: 1 },
        { team1: "SWE", team2: "SUI", score1: 4, score2: 0 },
        { team1: "FIN", team2: "CZE", score1: 2, score2: 1 },
      ],
      winner: "SWE",
    },
    {
      name: "Czech Hockey Games (Fortuna)",
      host: "Czech Republic",
      dates: "April 30 - May 3, 2026",
      matches: [
        { team1: "CZE", team2: "FIN", score1: 3, score2: 2 },
        { team1: "SWE", team2: "SUI", score1: 4, score2: 1 },
        { team1: "CZE", team2: "SWE", score1: 2, score2: 1, isShootout: true },
        { team1: "FIN", team2: "SUI", score1: 3, score2: 0 },
        { team1: "CZE", team2: "SUI", score1: 4, score2: 2 },
        { team1: "FIN", team2: "SWE", score1: 1, score2: 3 },
      ],
      winner: "CZE",
    },
  ],
  finalStandings: [
    { team: "SWE", gp: 6, w: 4, otw: 0, otl: 1, l: 1, gf: 18, ga: 9, pts: 13 },
    { team: "CZE", gp: 6, w: 3, otw: 1, otl: 1, l: 1, gf: 16, ga: 12, pts: 12 },
    { team: "FIN", gp: 6, w: 3, otw: 0, otl: 0, l: 3, gf: 15, ga: 14, pts: 9 },
    { team: "SUI", gp: 6, w: 0, otw: 0, otl: 0, l: 6, gf: 5, ga: 19, pts: 0 },
  ],
  winner: "SWE",
};

export const EURO_HOCKEY_TOUR_2024_25: EuroHockeyTour = {
  season: "2024-25",
  events: [
    {
      name: "Finnish Ice Hockey Games",
      host: "Finland",
      dates: "November 7-10, 2024",
      matches: [
        { team1: "FIN", team2: "SWE", score1: 3, score2: 2, isShootout: true },
        { team1: "CZE", team2: "SUI", score1: 4, score2: 1 },
        { team1: "FIN", team2: "CZE", score1: 2, score2: 3 },
        { team1: "SWE", team2: "SUI", score1: 5, score2: 2 },
        { team1: "FIN", team2: "SUI", score1: 4, score2: 0 },
        { team1: "SWE", team2: "CZE", score1: 2, score2: 1 },
      ],
      winner: "SWE",
    },
    {
      name: "Channel One Cup",
      host: "Russia",
      dates: "December 12-15, 2024",
      matches: [
        { team1: "FIN", team2: "SWE", score1: 2, score2: 4 },
        { team1: "CZE", team2: "SUI", score1: 3, score2: 2, isOvertime: true },
        { team1: "FIN", team2: "CZE", score1: 1, score2: 2 },
        { team1: "SWE", team2: "SUI", score1: 3, score2: 1 },
        { team1: "FIN", team2: "SUI", score1: 3, score2: 2 },
        { team1: "SWE", team2: "CZE", score1: 4, score2: 3, isShootout: true },
      ],
      winner: "SWE",
    },
    {
      name: "Beijer Hockey Games",
      host: "Sweden",
      dates: "February 6-9, 2025",
      matches: [
        { team1: "SWE", team2: "FIN", score1: 3, score2: 1 },
        { team1: "CZE", team2: "SUI", score1: 2, score2: 2, isShootout: true },
        { team1: "SWE", team2: "CZE", score1: 4, score2: 2 },
        { team1: "FIN", team2: "SUI", score1: 3, score2: 0 },
        { team1: "SWE", team2: "SUI", score1: 5, score2: 1 },
        { team1: "FIN", team2: "CZE", score1: 2, score2: 3 },
      ],
      winner: "SWE",
    },
    {
      name: "Czech Hockey Games",
      host: "Czech Republic",
      dates: "April 24-27, 2025",
      matches: [
        { team1: "CZE", team2: "FIN", score1: 0, score2: 0 },
        { team1: "SWE", team2: "SUI", score1: 0, score2: 0 },
        { team1: "CZE", team2: "SWE", score1: 0, score2: 0 },
        { team1: "FIN", team2: "SUI", score1: 0, score2: 0 },
        { team1: "CZE", team2: "SUI", score1: 0, score2: 0 },
        { team1: "FIN", team2: "SWE", score1: 0, score2: 0 },
      ],
    },
  ],
  finalStandings: [
    { team: "SWE", gp: 9, w: 7, otw: 1, otl: 0, l: 1, gf: 30, ga: 16, pts: 24 },
    { team: "CZE", gp: 9, w: 4, otw: 1, otl: 1, l: 3, gf: 20, ga: 18, pts: 15 },
    { team: "FIN", gp: 9, w: 4, otw: 0, otl: 1, l: 4, gf: 18, ga: 17, pts: 13 },
    { team: "SUI", gp: 9, w: 1, otw: 0, otl: 0, l: 8, gf: 10, ga: 27, pts: 3 },
  ],
  winner: "SWE",
};

export const EURO_HOCKEY_TOUR_2023_24: EuroHockeyTour = {
  season: "2023-24",
  events: [
    {
      name: "Finnish Ice Hockey Games",
      host: "Finland",
      dates: "November 9-12, 2023",
      matches: [
        { team1: "FIN", team2: "SWE", score1: 4, score2: 3, isOvertime: true },
        { team1: "CZE", team2: "SUI", score1: 5, score2: 2 },
        { team1: "FIN", team2: "CZE", score1: 3, score2: 1 },
        { team1: "SWE", team2: "SUI", score1: 4, score2: 0 },
        { team1: "FIN", team2: "SUI", score1: 6, score2: 1 },
        { team1: "SWE", team2: "CZE", score1: 3, score2: 2 },
      ],
      winner: "FIN",
    },
    {
      name: "Channel One Cup",
      host: "Russia",
      dates: "December 14-17, 2023",
      matches: [
        { team1: "FIN", team2: "SWE", score1: 1, score2: 2 },
        { team1: "CZE", team2: "SUI", score1: 4, score2: 3, isShootout: true },
        { team1: "FIN", team2: "CZE", score1: 2, score2: 1 },
        { team1: "SWE", team2: "SUI", score1: 5, score2: 2 },
        { team1: "FIN", team2: "SUI", score1: 4, score2: 1 },
        { team1: "SWE", team2: "CZE", score1: 3, score2: 2 },
      ],
      winner: "SWE",
    },
    {
      name: "Beijer Hockey Games",
      host: "Sweden",
      dates: "February 8-11, 2024",
      matches: [
        { team1: "SWE", team2: "FIN", score1: 2, score2: 1 },
        { team1: "CZE", team2: "SUI", score1: 3, score2: 1 },
        { team1: "SWE", team2: "CZE", score1: 4, score2: 2 },
        { team1: "FIN", team2: "SUI", score1: 5, score2: 0 },
        { team1: "SWE", team2: "SUI", score1: 6, score2: 2 },
        { team1: "FIN", team2: "CZE", score1: 2, score2: 3 },
      ],
      winner: "SWE",
    },
    {
      name: "Czech Hockey Games",
      host: "Czech Republic",
      dates: "April 25-28, 2024",
      matches: [
        { team1: "CZE", team2: "FIN", score1: 4, score2: 2 },
        { team1: "SWE", team2: "SUI", score1: 3, score2: 1 },
        { team1: "CZE", team2: "SWE", score1: 2, score2: 3, isShootout: true },
        { team1: "FIN", team2: "SUI", score1: 4, score2: 0 },
        { team1: "CZE", team2: "SUI", score1: 5, score2: 1 },
        { team1: "FIN", team2: "SWE", score1: 1, score2: 2 },
      ],
      winner: "SWE",
    },
  ],
  finalStandings: [
    { team: "SWE", gp: 12, w: 9, otw: 1, otl: 0, l: 2, gf: 38, ga: 19, pts: 30 },
    { team: "FIN", gp: 12, w: 7, otw: 1, otl: 0, l: 4, gf: 35, ga: 18, pts: 24 },
    { team: "CZE", gp: 12, w: 5, otw: 1, otl: 1, l: 5, gf: 32, ga: 27, pts: 18 },
    { team: "SUI", gp: 12, w: 0, otw: 0, otl: 1, l: 11, gf: 14, ga: 55, pts: 1 },
  ],
  winner: "SWE",
};

export const EURO_HOCKEY_TOUR_HISTORY: { season: string; winner: string }[] = [
  { season: "2024-25", winner: "SWE" },
  { season: "2023-24", winner: "SWE" },
  { season: "2022-23", winner: "SWE" },
  { season: "2021-22", winner: "FIN" },
  { season: "2020-21", winner: "FIN" },
  { season: "2019-20", winner: "SWE" },
  { season: "2018-19", winner: "SWE" },
  { season: "2017-18", winner: "SWE" },
  { season: "2016-17", winner: "CZE" },
  { season: "2015-16", winner: "FIN" },
  { season: "2014-15", winner: "RUS" },
];

export const getEuroHockeyTour = (season: string): EuroHockeyTour | undefined => {
  if (season === "2025-26") return EURO_HOCKEY_TOUR_2025_26;
  if (season === "2024-25") return EURO_HOCKEY_TOUR_2024_25;
  if (season === "2023-24") return EURO_HOCKEY_TOUR_2023_24;
  return undefined;
};

export const getTournament = (type: TournamentType, year: number): InternationalTournament | undefined => {
  switch (type) {
    case "WC":
      if (year === 2026) return WORLD_CHAMPIONSHIP_2026;
      if (year === 2025) return WORLD_CHAMPIONSHIP_2025;
      if (year === 2024) return WORLD_CHAMPIONSHIP_2024;
      if (year === 2023) return WORLD_CHAMPIONSHIP_2023;
      break;
    case "Olympics":
      if (year === 2026) return OLYMPICS_2026;
      if (year === 2022) return OLYMPICS_2022;
      if (year === 2018) return OLYMPICS_2018;
      if (year === 2014) return OLYMPICS_2014;
      break;
    case "WJC":
      if (year === 2026) return WORLD_JUNIORS_2026;
      if (year === 2025) return WORLD_JUNIORS_2025;
      if (year === 2024) return WORLD_JUNIORS_2024;
      break;
    case "U18WC":
      if (year === 2026) return U18_WORLD_CHAMPIONSHIP_2026;
      if (year === 2025) return U18_WORLD_CHAMPIONSHIP_2025;
      if (year === 2024) return U18_WORLD_CHAMPIONSHIP_2024;
      break;
    case "WWC":
      if (year === 2026) return WOMEN_WORLD_CHAMPIONSHIP_2026;
      if (year === 2025) return WOMEN_WORLD_CHAMPIONSHIP_2025;
      if (year === 2024) return WOMEN_WORLD_CHAMPIONSHIP_2024;
      if (year === 2023) return WOMEN_WORLD_CHAMPIONSHIP_2023;
      break;
    case "WOlympics":
      if (year === 2026) return WOMEN_OLYMPICS_2026;
      if (year === 2022) return WOMEN_OLYMPICS_2022;
      if (year === 2018) return WOMEN_OLYMPICS_2018;
      if (year === 2014) return WOMEN_OLYMPICS_2014;
      break;
    case "U18WWC":
      if (year === 2026) return U18_WOMEN_WORLD_CHAMPIONSHIP_2026;
      if (year === 2025) return U18_WOMEN_WORLD_CHAMPIONSHIP_2025;
      if (year === 2024) return U18_WOMEN_WORLD_CHAMPIONSHIP_2024;
      break;
  }
  return undefined;
};

export const getTournamentHistory = (type: TournamentType): HistoricalMedal[] => {
  switch (type) {
    case "WC":
      return WORLD_CHAMPIONSHIP_HISTORY;
    case "Olympics":
      return OLYMPICS_HISTORY;
    case "WJC":
      return WORLD_JUNIORS_HISTORY;
    case "U18WC":
      return U18_WORLD_CHAMPIONSHIP_HISTORY;
    case "WWC":
      return WOMEN_WORLD_CHAMPIONSHIP_HISTORY;
    case "WOlympics":
      return WOMEN_OLYMPICS_HISTORY;
    case "U18WWC":
      return U18_WOMEN_WORLD_CHAMPIONSHIP_HISTORY;
    default:
      return [];
  }
};
